import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import AIAssist from './pages/AIAssist';
import FormBuilder from './FormBuilder';
import ReferralsManager from './ReferralsManager';
import CrmCsvImport from './CrmCsvImport';
import CrmCsvExport from './CrmCsvExport';
import { FORM_TEMPLATES, buildTemplateFields } from './formTemplates';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL });
API.interceptors.request.use(config => {
  const token = localStorage.getItem('cms_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const FIELD_TYPES = [
  { type: 'string',  icon: 'Aa', label: 'String',  desc: 'Short text' },
  { type: 'text',    icon: '¶',  label: 'Text',    desc: 'Long paragraph' },
  { type: 'number',  icon: '##', label: 'Number',  desc: 'Integer or decimal' },
  { type: 'image',   icon: '⊡',  label: 'Image',   desc: 'Image URL' },
  { type: 'video',   icon: '▶',  label: 'Video',   desc: 'Video URL or file' },
  { type: 'file',    icon: '📎', label: 'File',    desc: 'Document or file upload' },
  { type: 'boolean', icon: '◐',  label: 'Boolean', desc: 'True / False' },
  { type: 'date',    icon: '▦',  label: 'Date',    desc: 'Date value' },
  { type: 'array',   icon: '❏',  label: 'Array',   desc: 'List of objects' },
  { type: 'html',    icon: '<>', label: 'HTML',    desc: 'Rich HTML editor' },
];

const uid = () => Math.random().toString(36).slice(2, 8);
const slugify = s => s.toLowerCase().replace(/\s+/g, '-');

// ── Speaker autocomplete ────────────────────────────────────────────────────
// Names come from the "Si Her DeFi (Base)" page → Speakers section → `speakers` list.
const SpeakerNamesContext = React.createContext({ names: [], refresh: () => {} });
const isSpeakerField = sf => sf?.type === 'string' && /^speakers?$/i.test(String(sf.name || '').trim());
const normPageName = v => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const collectSpeakerNames = (node, out) => {
  if (!node) return;
  const list = node.data?.speakers;
  if (Array.isArray(list)) {
    list.forEach(item => {
      const name = typeof item === 'string'
        ? item
        : (item?.name || item?.title || Object.values(item || {}).find(v => typeof v === 'string' && v && !/^https?:|^\/|^ipfs/i.test(v)));
      if (name && String(name).trim()) out.push(String(name).trim());
    });
  }
  (node.children || []).forEach(c => collectSpeakerNames(c, out));
};

async function fetchSpeakerNames(pages) {
  const defi = (pages || []).filter(pg => normPageName(pg.name).includes('defi'));
  const page = defi.find(pg => normPageName(pg.name).includes('base')) || defi[0];
  if (!page) return [];
  const res = await API.get(`/api/documents?schemaId=${page._id}`);
  const out = [];
  (res.data || []).forEach(doc => {
    const tree = doc.data?.tree;
    const walk = n => {
      if (!n) return;
      if (normPageName(n.label || n.name).includes('speaker')) { collectSpeakerNames(n, out); return; }
      (n.children || []).forEach(walk);
    };
    if (normPageName(doc.title || doc.label).includes('speaker')) collectSpeakerNames(tree, out);
    else walk(tree);
  });
  return [...new Set(out)].sort((a, b) => a.localeCompare(b));
}

function SpeakerNameInput({ value, onChange, placeholder }) {
  const { names, refresh } = React.useContext(SpeakerNamesContext);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = String(value || '').trim().toLowerCase();
  const matches = names
    .filter(n => n.toLowerCase().includes(q) && n.toLowerCase() !== q)
    .sort((a, b) => (b.toLowerCase().startsWith(q) ? 1 : 0) - (a.toLowerCase().startsWith(q) ? 1 : 0))
    .slice(0, 8);
  const pick = n => { onChange(n); setOpen(false); };
  return (
    <div style={{position:'relative'}}>
      <input className="minput" type="text" value={value || ''} placeholder={placeholder} autoComplete="off"
        onFocus={() => { refresh(); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        onChange={e => { onChange(e.target.value); setOpen(true); setActive(0); }}
        onKeyDown={e => {
          if (!open || !matches.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a + 1) % matches.length); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a - 1 + matches.length) % matches.length); }
          else if (e.key === 'Enter') { e.preventDefault(); pick(matches[active] || matches[0]); }
          else if (e.key === 'Escape') setOpen(false);
        }} />
      {open && matches.length > 0 && (
        <div style={{position:'absolute',left:0,right:0,top:'100%',zIndex:50,marginTop:2,maxHeight:200,overflowY:'auto',background:'var(--bg2, #1a1a1a)',border:'1px solid var(--border2)',borderRadius:'var(--r-sm)',boxShadow:'0 6px 18px rgba(0,0,0,.4)'}}>
          {matches.map((n, i) => (
            <div key={n} onMouseDown={e => { e.preventDefault(); pick(n); }} onMouseEnter={() => setActive(i)}
              style={{padding:'7px 10px',fontSize:13,cursor:'pointer',color:'var(--text)',background:i === active ? 'var(--border)' : 'transparent'}}>
              {n}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
const COLORS = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899'];
const typeIcon = type => FIELD_TYPES.find(f => f.type === type)?.icon || '?';

function getNodeByPath(tree, path) {
  let node = tree;
  for (const id of path) {
    node = (node.children || []).find(c => c.id === id);
    if (!node) return null;
  }
  return node;
}

function updateNodeByPath(tree, path, updater) {
  if (path.length === 0) return updater(tree);
  return {
    ...tree,
    children: (tree.children || []).map(child =>
      child.id === path[0]
        ? updateNodeByPath(child, path.slice(1), updater)
        : child
    )
  };
}

function previewValue(field, value) {
  if (value === undefined || value === null || value === '') return <span className="preview-empty">Empty</span>;
  if (field.type === 'boolean') return <span className="preview-val">{value ? 'True' : 'False'}</span>;
  if (field.type === 'html') return <span className="preview-val" style={{fontSize:11,color:'var(--accent)'}}>HTML content</span>;
  if (field.type === 'video') return (
    <span className="preview-val">
      {value ? <span style={{fontSize:11,color:'var(--accent)'}}>▶ Video set</span> : <span className="preview-empty">Empty</span>}
    </span>
  );
  if (field.type === 'file') return (
    <span className="preview-val">
      {value ? <span style={{fontSize:11,color:'var(--accent)'}}>📎 File set</span> : <span className="preview-empty">Empty</span>}
    </span>
  );
  if (field.type === 'image') return (
    <span className="preview-val">
      <img src={value} alt="" style={{width:20,height:20,objectFit:'cover',borderRadius:3}} onError={e=>e.target.style.display='none'} />
      <span style={{opacity:.5,fontSize:11,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{value}</span>
    </span>
  );
  if (field.type === 'array') {
    const arr = Array.isArray(value) ? value : [];
    if (!arr.length) return <span className="preview-empty">Empty</span>;

    const displayFieldName = field.displayField;
    const firstItem = arr[0];
    let displayValue = firstItem?.[displayFieldName];

    if (!displayValue || displayValue === '') {
      displayValue = '(empty)';
    } else if (String(displayValue).length > 40) {
      displayValue = String(displayValue).substring(0, 40) + '...';
    }

    return (
      <span className="preview-val" style={{fontSize:11}}>
        {arr.length} item{arr.length !== 1 ? 's' : ''}{displayFieldName && ` • ${displayValue}`}
      </span>
    );
  }
  return <span className="preview-val" style={{maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{String(value)}</span>;
}

function Modal({ onClose, children, width = 420 }) {
  return (
    <div className="overlay">
      <div className="modal" style={{ width }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', busyLabel, busy = false, onConfirm, onCancel }) {
  return (
    <Modal onClose={onCancel}>
      <div className="modal-title">{title}</div>
      <div style={{fontSize:13,color:'var(--muted)',lineHeight:1.6}}>{message}</div>
      <div className="mactions">
        <button className="btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn-solid" onClick={onConfirm} disabled={busy}>
          {busy && <span className="spinner" aria-hidden="true" />}
          {busy && busyLabel ? busyLabel : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

function NameModal({ title, placeholder, onConfirm, onClose, showKey = false, confirmLabel = 'Create', initialValue = '' }) {
  const [name, setName] = useState(initialValue);
  const [key, setKey] = useState('');
  const [keyError, setKeyError] = useState('');
  const autoKey = name.trim() ? name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') : '';
  const finalKey = key.trim() || autoKey;

  const handleNameChange = (val) => {
    setName(val);
    setKeyError('');
  };

  const handleConfirm = () => {
    if (!name.trim()) return;
    setKeyError('');
    onConfirm(name.trim(), finalKey, (err) => setKeyError(err));
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">{title}</div>
      <label className="mlabel">Name</label>
      <input className="minput" value={name} onChange={e => handleNameChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleConfirm()}
        placeholder={placeholder} autoFocus />
      {showKey && name.trim() && (
        <>
          <label className="mlabel" style={{marginTop:14}}>Key <span style={{color:'var(--muted)',fontWeight:400,textTransform:'none',letterSpacing:0}}>(auto-generated, you can change it)</span></label>
          <input className="minput" style={{fontFamily:'var(--mono)',fontSize:12,borderColor: keyError ? 'var(--red)' : ''}}
            value={key || autoKey}
            onChange={e => { setKey(e.target.value); setKeyError(''); }}
            placeholder={autoKey} />
          {keyError
            ? <div style={{fontSize:11,color:'var(--red)',marginTop:4}}>⚠ {keyError}</div>
            : <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>This _key identifies this section in the API.</div>
          }
        </>
      )}
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={handleConfirm}>{confirmLabel}</button>
      </div>
    </Modal>
  );
}

function DuplicateFormModal({ form, onConfirm, onClose }) {
  const [name, setName] = useState(`${form?.title || 'Form'} - Copy`);
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await onConfirm(name.trim(), slug.trim() || null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">Duplicate Form</div>
      <p className="modal-sub">Creating a copy of "<strong>{form?.title}</strong>"</p>

      <label className="mlabel">New Form Name</label>
      <input className="minput" value={name} onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleConfirm()}
        placeholder="e.g. Contact Form - Copy" autoFocus />

      <label className="mlabel" style={{marginTop:14}}>URL Slug <span style={{color:'var(--muted)',fontWeight:400,textTransform:'none',letterSpacing:0}}>(optional, auto-generated if empty)</span></label>
      <input className="minput" style={{fontFamily:'var(--mono)',fontSize:12}}
        value={slug}
        onChange={e => setSlug(e.target.value)}
        placeholder="e.g. contact-form-copy" />
      <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Leave empty to auto-generate a unique slug</div>

      <div className="mactions">
        <button className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
        <button className="btn-solid" onClick={handleConfirm} disabled={loading || !name.trim()}>
          {loading ? 'Duplicating...' : 'Duplicate'}
        </button>
      </div>
    </Modal>
  );
}

function AddFormModal({ onConfirm, onClose }) {
  const [name, setName] = useState('Si Her DeFi Application');
  const [templateId, setTemplateId] = useState('si-her-defi');

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = FORM_TEMPLATES[id];
    if (t?.title) setName(t.title);
    else if (id === 'blank') setName('');
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">New Form</div>
      <label className="mlabel">Template</label>
      <select className="minput" value={templateId} onChange={e => applyTemplate(e.target.value)}>
        {Object.values(FORM_TEMPLATES).map(t => (
          <option key={t.id} value={t.id}>{t.label}</option>
        ))}
      </select>
      <label className="mlabel" style={{marginTop:14}}>Form Name</label>
      <input className="minput" value={name} onChange={e => setName(e.target.value)}
        placeholder="e.g. Si Her DeFi Application" autoFocus />
      <div style={{fontSize:12,color:'var(--muted)',marginTop:8}}>
        Fields stay fully editable in the CMS — including reordering.
      </div>
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={() => name.trim() && onConfirm(name.trim(), templateId)}>Create</button>
      </div>
    </Modal>
  );
}

function ChangePasswordModal({ onClose }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    if (!currentPassword || !newPassword) return setError('Fill in all fields');
    if (newPassword.length < 8) return setError('New password must be at least 8 characters');
    if (newPassword !== confirmPassword) return setError('New passwords do not match');
    setLoading(true);
    try {
      await API.post('/api/auth/change-password', { currentPassword, newPassword });
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
    setLoading(false);
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">Change Password</div>
      {error && <div style={{color:'#fca5a5',fontSize:13,marginBottom:10}}>{error}</div>}
      {success && <div style={{color:'#34d399',fontSize:13,marginBottom:10}}>{success}</div>}
      <label className="mlabel">Current Password</label>
      <input className="minput" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
      <label className="mlabel" style={{marginTop:12}}>New Password</label>
      <input className="minput" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
      <label className="mlabel" style={{marginTop:12}}>Confirm New Password</label>
      <input className="minput" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Close</button>
        <button className="btn-solid" onClick={handleSubmit} disabled={loading}>{loading ? 'Saving…' : 'Update Password'}</button>
      </div>
    </Modal>
  );
}

function EditFieldLabelModal({ field, onConfirm, onClose }) {
  const [label, setLabel] = useState(field?.label || '');

  const handleConfirm = () => {
    if (!label.trim()) return;
    onConfirm(label.trim());
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">Edit Field Label</div>
      <label className="mlabel">Display Name</label>
      <input className="minput" value={label} onChange={e => setLabel(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleConfirm()}
        placeholder="Enter field display name" autoFocus />
      <div style={{fontSize:11,color:'var(--muted)',marginTop:8}}>
        Field key "{field?.name}" will remain unchanged
      </div>
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={handleConfirm}>Update</button>
      </div>
    </Modal>
  );
}

function EditArrayDisplayFieldModal({ field, onConfirm, onClose }) {
  const [displayField, setDisplayField] = useState(field?.displayField || '');
  const subFields = field?.subFields || [];

  const handleConfirm = () => {
    if (!displayField.trim()) return;
    onConfirm(displayField.trim());
  };

  return (
    <Modal onClose={onClose}>
      <div className="modal-title">Array Display Field</div>
      <p style={{fontSize:12,color:'var(--muted)',marginBottom:12}}>
        Select which field to show in array preview
      </p>
      <label className="mlabel">Display Field</label>
      <select className="minput" value={displayField} onChange={e => setDisplayField(e.target.value)}>
        <option value="">— Select a field —</option>
        {subFields.map(sf => (
          <option key={sf.id} value={sf.name}>{sf.label} ({sf.type})</option>
        ))}
      </select>
      <div style={{fontSize:11,color:'var(--muted)',marginTop:12,padding:8,background:'var(--bg)',borderRadius:4}}>
        The selected field value will be shown in array item previews.
      </div>
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={handleConfirm} disabled={!displayField.trim()}>Update</button>
      </div>
    </Modal>
  );
}

function AddFieldModal({ onConfirm, onClose }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [type, setType] = useState('string');
  const [subFields, setSubFields] = useState([]);
  const [sfName, setSfName] = useState('');
  const [sfType, setSfType] = useState('string');
  const [displayField, setDisplayField] = useState('');

  const handleNext = () => {
    if (!name.trim()) return;
    if (type === 'array') { setStep(2); return; }
    onConfirm({ id: uid(), name: name.trim(), label: name.trim(), type });
  };
  const addSubField = () => {
    if (!sfName.trim()) return;
    setSubFields(p => [...p, { id: uid(), name: sfName.trim(), label: sfName.trim(), type: sfType }]);
    setSfName(''); setSfType('string');
  };

  const handleConfirmArray = () => {
    if (subFields.length === 0 || !displayField.trim()) return;
    const fieldToCreate = { id: uid(), name: name.trim(), label: name.trim(), type: 'array', subFields, displayField };
    console.log('AddFieldModal handleConfirmArray - Creating field:', fieldToCreate);
    console.log('SubFields with preview:', subFields);
    onConfirm(fieldToCreate);
  };

  return (
    <Modal onClose={onClose} width={480}>
      {step === 1 && <>
        <div className="modal-title">Add Data Field</div>
        <label className="mlabel">Field Name</label>
        <input className="minput" value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleNext()}
          placeholder="e.g. title, description, image..." autoFocus />
        <label className="mlabel" style={{marginTop:16}}>Field Type</label>
        <div className="ftype-grid">
          {FIELD_TYPES.map(f => (
            <div key={f.type} className={`ftype-card ${type === f.type ? 'sel' : ''}`} onClick={() => setType(f.type)}>
              <div className="ftype-icon">{f.icon}</div>
              <div className="ftype-label">{f.label}</div>
              <div className="ftype-desc">{f.desc}</div>
            </div>
          ))}
        </div>
        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={handleNext}>{type === 'array' ? 'Next →' : 'Add Field'}</button>
        </div>
      </>}
      {step === 2 && <>
        <div className="modal-title">Array Structure — <span className="accent">{name}</span></div>
        <p className="modal-sub">Each item in this array will contain:</p>
        <div className="sf-builder">
          <div className="sf-row">
            <input className="minput" style={{flex:1}} value={sfName} onChange={e => setSfName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSubField()} placeholder="Sub-field name..." />
            <select className="mselect" value={sfType} onChange={e => setSfType(e.target.value)}>
              {FIELD_TYPES.filter(f => f.type !== 'array').map(f => <option key={f.type} value={f.type}>{f.label}</option>)}
            </select>
            <button className="btn-solid" style={{padding:'9px 16px'}} onClick={addSubField}>+</button>
          </div>
          {subFields.length > 0 && (
            <div className="sf-list">
              {subFields.map((sf, i) => (
                <div key={sf.id} className="sf-item" style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:200}}>
                    <span className="sf-name">{sf.name}</span>
                    <span className="badge">{sf.type}</span>
                  </div>
                  {sf.type === 'string' && (
                    <>
                      <button style={{fontSize:11,padding:'4px 10px',whiteSpace:'nowrap',background:sf.previewTemplate?'var(--accent)':'transparent',color:sf.previewTemplate?'white':'var(--text)',border:'1px solid '+(sf.previewTemplate?'var(--accent)':'var(--border)'),borderRadius:'4px',cursor:'pointer'}}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          // Only enable preview if not already enabled
                          if (!sf.previewTemplate || !sf.previewTemplate.trim()) {
                            setSubFields(p => {
                              return p.map((f, j) => {
                                if (j === i) {
                                  const newSf = { ...f };
                                  newSf.previewTemplate = f.label ? (f.label + ': ') : 'Item: ';
                                  return newSf;
                                }
                                return f;
                              });
                            });
                          }
                        }}>
                        {sf.previewTemplate && sf.previewTemplate.trim() ? '✓ Preview' : '+ Preview'}
                      </button>
                      {sf.previewTemplate && (
                        <>
                          <input type="text" style={{fontSize:10,padding:'3px 6px',border:'1px solid var(--border)',borderRadius:'3px',flex:1,minWidth:80,maxWidth:150}}
                            value={sf.previewTemplate}
                            onChange={e => {
                              const newSf = { ...sf, previewTemplate: e.target.value };
                              setSubFields(p => {
                                return p.map((f, j) => j === i ? newSf : f);
                              });
                            }}
                            placeholder="Template..." />
                          <button style={{fontSize:10,padding:'2px 6px',minWidth:'30px',color:'var(--muted)'}} className="btn-ghost" onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSubFields(p => {
                              return p.map((f, j) => {
                                if (j === i) {
                                  const newSf = { ...f };
                                  newSf.previewTemplate = '';
                                  return newSf;
                                }
                                return f;
                              });
                            });
                          }}>✕</button>
                        </>
                      )}
                    </>
                  )}
                  <button className="rm-btn" onClick={() => setSubFields(p => p.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        {subFields.length > 0 && (
          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <label className="mlabel">Display Field in Preview <span style={{color:'var(--accent)',fontWeight:600}}>*</span></label>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12 }}>Choose which field to show when previewing array items</p>
            <select className="minput" value={displayField} onChange={e => setDisplayField(e.target.value)}>
              <option value="">— Select a field —</option>
              {subFields.map(sf => (
                <option key={sf.id} value={sf.name}>{sf.label} ({sf.type})</option>
              ))}
            </select>
          </div>
        )}
        <div className="mactions">
          <button className="btn-ghost" onClick={() => setStep(1)}>← Back</button>
          <button className="btn-solid" onClick={handleConfirmArray} disabled={subFields.length === 0 || !displayField.trim()}>
            Add Array Field
          </button>
        </div>
      </>}
    </Modal>
  );
}

function NestedImageArrayEditor({ value, onChange, idPrefix, subFields = [] }) {
  const images = Array.isArray(value) ? value : [];

  const uploadImage = async event => {
    const file = event.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await fetch(process.env.REACT_APP_API_URL + '/api/upload', { method: 'POST', body: fd });
      const data = await res.json();
      if (data.url) {
        const nextItem = subFields.length ? { [subFields[0].name]: data.url } : data.url;
        onChange([...images, nextItem]);
      }
    } catch (err) {
      alert('Upload failed');
    }
    event.target.value = '';
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
        onClick={() => document.getElementById(idPrefix)?.click()}>
        <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; Add logo</span>
        <input id={idPrefix} type="file" accept="image/*" style={{display:'none'}} onChange={uploadImage} />
      </div>
      {images.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:8}}>
          {images.map((item, index) => {
            const image = subFields.length ? item?.[subFields[0]?.name] : item;
            return (
              <div key={`${image}-${index}`} style={{position:'relative'}}>
                {image ? <img src={image} alt="" style={{width:100,height:56,objectFit:'contain',borderRadius:6,background:'var(--bg)'}} onError={e => e.target.style.display='none'} /> : <span className="preview-empty">Empty</span>}
                <button type="button" className="btn-ghost" style={{fontSize:11,padding:'3px 6px',marginTop:4}} onClick={() => onChange(images.filter((_, imageIndex) => imageIndex !== index))}>Remove</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NestedSpeakerArrayEditor({ value, onChange, idPrefix }) {
  const speakers = Array.isArray(value) ? value : [];

  const updateSpeaker = (index, key, nextValue) => {
    onChange(speakers.map((speaker, speakerIndex) =>
      speakerIndex === index ? { ...speaker, [key]: nextValue } : speaker
    ));
  };

  const uploadSpeakerImage = async (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    try {
      const response = await fetch(process.env.REACT_APP_API_URL + '/api/upload', {
        method: 'POST',
        body: formData
      });
      if (!response.ok) throw new Error(`Upload failed (${response.status})`);
      const data = await response.json();
      if (!data.url) throw new Error('Upload did not return an image URL');
      updateSpeaker(index, 'image', data.url);
    } catch (error) {
      alert(error.message || 'Upload failed');
    }
    event.target.value = '';
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:10}}>
      {speakers.map((speaker, index) => (
        <div key={`${idPrefix}-${index}`} style={{padding:10,border:'1px solid var(--border)',borderRadius:6}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <span className="badge">Speaker {index + 1}</span>
            <button type="button" className="btn-ghost" onClick={() => onChange(speakers.filter((_, speakerIndex) => speakerIndex !== index))}>Remove</button>
          </div>
          <input className="minput" value={speaker?.name || ''} onChange={event => updateSpeaker(index, 'name', event.target.value)} placeholder="Speaker name" />
          <input className="minput" style={{marginTop:8}} value={speaker?.role || ''} onChange={event => updateSpeaker(index, 'role', event.target.value)} placeholder="Speaker role / organisation" />
          <div style={{marginTop:8}}>
            <label className="btn-ghost" htmlFor={`${idPrefix}-image-${index}`} style={{display:'inline-block',cursor:'pointer'}}>
              {speaker?.image ? 'Replace speaker image' : 'Upload speaker image'}
            </label>
            <input id={`${idPrefix}-image-${index}`} type="file" accept="image/*" style={{display:'none'}} onChange={event => uploadSpeakerImage(index, event)} />
            {speaker?.image && <img src={speaker.image} alt="" style={{display:'block',width:80,height:80,objectFit:'cover',borderRadius:8,marginTop:8}} />}
          </div>
        </div>
      ))}
      <button type="button" className="add-item-btn" onClick={() => onChange([...speakers, { name: '', role: '', image: '' }])}>+ Add speaker</button>
    </div>
  );
}

function NestedArrayFieldEditor({ value, onChange, idPrefix, subFields = [] }) {
  const isSpeakerList = subFields.some(field => field.name === 'name')
    && subFields.some(field => field.name === 'role')
    && subFields.some(field => field.name === 'image');
  return isSpeakerList
    ? <NestedSpeakerArrayEditor value={value} onChange={onChange} idPrefix={idPrefix} />
    : <NestedImageArrayEditor value={value} onChange={onChange} idPrefix={idPrefix} subFields={subFields} />;
}

function AddArrayItemModal({ field, onConfirm, onClose }) {
  const [vals, setVals] = useState({});
  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));
  return (
    <Modal onClose={onClose} width={460}>
      <div className="modal-title">Add Item — <span className="accent">{field.label}</span></div>
      {(field.subFields || []).map(sf => (
        <div key={sf.id} style={{marginBottom:14}}>
          <label className="mlabel">{sf.label} <span className="badge">{sf.type}</span></label>
          {sf.type === 'text' ? <textarea className="minput" rows={3} value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} placeholder={`Enter ${sf.label}...`} />
          : sf.type === 'html' ? (
            <ReactQuill theme="snow" value={vals[sf.name]||''} onChange={v => set(sf.name, v)} style={{background:'white',borderRadius:6}} />
          ) : sf.type === 'array' ? (
            <NestedArrayFieldEditor value={vals[sf.name]} onChange={value => set(sf.name, value)} idPrefix={'up-array-' + sf.name} subFields={sf.subFields || []} />
          ) : sf.type === 'boolean' ? (
            <div className="bool-toggle">
              <button className={`bool-btn ${vals[sf.name] ? 'on' : ''}`} onClick={() => set(sf.name, true)}>True</button>
              <button className={`bool-btn ${!vals[sf.name] ? 'on' : ''}`} onClick={() => set(sf.name, false)}>False</button>
            </div>
          ) : sf.type === 'video' ? (
            <div>
              <input className="minput" value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} placeholder="Paste video URL or upload..." style={{marginBottom:6}} />
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                onClick={() => document.getElementById('vid-'+sf.name)?.click()}>
                <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; Upload video</span>
                <input id={'vid-'+sf.name} type="file" accept="video/*" style={{display:'none'}}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('image', file);
                    try {
                      const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST', body:fd});
                      const data = await res.json();
                      if (data.url) set(sf.name, data.url);
                    } catch(err) { alert('Upload failed'); }
                  }} />
              </div>
              {vals[sf.name] && <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:6}}>
                <video src={vals[sf.name]} controls style={{width:'100%',maxHeight:100,borderRadius:6}} />
                <button className="btn-ghost" style={{fontSize:11,padding:'4px 8px',width:'fit-content'}} onClick={() => set(sf.name, '')}>Remove video</button>
              </div>}
            </div>
          ) : sf.type === 'file' ? (
            <div>
              <input className="minput" value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} placeholder="Paste file URL or upload..." style={{marginBottom:6}} />
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                onClick={() => document.getElementById('file-'+sf.name)?.click()}>
                <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; Upload file</span>
                <input id={'file-'+sf.name} type="file" style={{display:'none'}}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('image', file);
                    try {
                      const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST', body:fd});
                      const data = await res.json();
                      if (data.url) set(sf.name, data.url);
                    } catch(err) { alert('Upload failed'); }
                  }} />
              </div>
              {vals[sf.name] && <div style={{marginTop:6,display:'flex',gap:6,alignItems:'center'}}>
                <div style={{fontSize:11,color:'var(--accent)'}}>✓ {vals[sf.name].split('/').pop()}</div>
                <button className="btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={() => set(sf.name, '')}>Remove</button>
              </div>}
            </div>
          ) : sf.type === 'image' ? (
            <div>
              <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                onClick={() => document.getElementById('up-'+sf.name)?.click()}>
                <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {vals[sf.name] ? 'Replace image' : 'Upload image'}</span>
                <input id={'up-'+sf.name} type="file" accept="image/*" style={{display:'none'}}
                  onChange={async e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append('image', file);
                    try {
                      const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST', body:fd});
                      const data = await res.json();
                      if (data.url) set(sf.name, data.url);
                    } catch(err) { alert('Upload failed'); }
                  }} />
              </div>
              {vals[sf.name] && <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:6}}>
                <img src={vals[sf.name]} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6}} onError={e => e.target.style.display='none'} />
                <button className="btn-ghost" style={{fontSize:11,padding:'4px 8px',width:'fit-content'}} onClick={() => set(sf.name, '')}>Remove image</button>
              </div>}
            </div>
          ) : sf.type === 'string' ? (
            <div>
              {isSpeakerField(sf)
                ? <SpeakerNameInput value={vals[sf.name]} onChange={v => set(sf.name, v)} placeholder={`Enter ${sf.label}...`} />
                : <input className="minput" type="text"
                value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)}
                placeholder={`Enter ${sf.label}...`} />}
              {console.log(`String field ${sf.name}: previewTemplate="${sf.previewTemplate}", value="${vals[sf.name]}", shouldShow=${!!(sf.previewTemplate && sf.previewTemplate.trim() && vals[sf.name])}`)}
              {sf.previewTemplate && sf.previewTemplate.trim() && vals[sf.name] && (
                <div style={{marginTop:8,padding:8,background:'var(--bg)',borderRadius:4,fontSize:12,color:'var(--muted2)',border:'1px solid var(--border)'}}>
                  <div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Preview:</div>
                  {(() => {
                    const previewText = sf.previewTemplate + vals[sf.name];
                    const isUrl = previewText.startsWith('http://') || previewText.startsWith('https://');
                    return isUrl ? (
                      <a href={previewText} target="_blank" rel="noopener noreferrer" style={{fontSize:13,color:'var(--accent)',textDecoration:'underline',wordBreak:'break-all',cursor:'pointer'}}>
                        {previewText}
                      </a>
                    ) : (
                      <div style={{fontSize:13,color:'var(--text)',wordBreak:'break-all'}}>
                        {previewText}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : <input className="minput" type={sf.type==='number'?'number':sf.type==='date'?'date':'text'}
              value={vals[sf.name]||''} onChange={e => set(sf.name, sf.type==='number' ? parseFloat(e.target.value)||0 : e.target.value)}
              placeholder={`Enter ${sf.label}...`} />}
        </div>
      ))}
      <div className="mactions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={() => onConfirm({ ...vals })}>Add Item</button>
      </div>
    </Modal>
  );
}

function EditFieldModal({ field, value, onSave, onAutoSave, onEditSubFields, onClose }) {
  const [val, setVal] = useState(value !== undefined ? value : field.type==='array'?[]:field.type==='boolean'?false:field.type==='number'?0:'');
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemVals, setEditingItemVals] = useState({});
  const [autoSaved, setAutoSaved] = useState(false);
  const autoSaveTimer = React.useRef(null);

  React.useEffect(() => {
    clearTimeout(autoSaveTimer.current);
    if (!onAutoSave) return;
    autoSaveTimer.current = setTimeout(() => {
      onAutoSave(val);
      setAutoSaved(true);
      setTimeout(() => setAutoSaved(false), 2000);
    }, 1500);
    return () => clearTimeout(autoSaveTimer.current);
  }, [val, onAutoSave]);
  return (
    <Modal onClose={onClose} width={500}>
      <div className="modal-title">
        <span style={{fontFamily:'var(--mono)',fontSize:14,color:'var(--muted2)'}}>{typeIcon(field.type)}</span>
        {field.label}
        <span className="badge" style={{marginLeft:4}}>{field.type}</span>
      </div>
      {field.type === 'string' && <input className="minput" value={val} onChange={e => setVal(e.target.value)} autoFocus />}
      {field.type === 'text' && <textarea className="minput" rows={7} value={val} onChange={e => setVal(e.target.value)} autoFocus />}
      {field.type === 'html' && (
        <ReactQuill theme="snow" value={val} onChange={setVal} style={{background:'white',borderRadius:6,marginTop:8}} />
      )}
      {field.type === 'number' && <input className="minput" type="number" value={val} onChange={e => setVal(parseFloat(e.target.value)||0)} autoFocus />}
      {field.type === 'date' && <input className="minput" type="date" value={val} onChange={e => setVal(e.target.value)} autoFocus />}
      {field.type === 'boolean' && (
        <div className="bool-toggle">
          <button className={`bool-btn ${val ? 'on' : ''}`} onClick={() => setVal(true)}>True</button>
          <button className={`bool-btn ${!val ? 'on' : ''}`} onClick={() => setVal(false)}>False</button>
        </div>
      )}
      {field.type === 'video' && (
        <div>
          <input className="minput" value={val} onChange={e => setVal(e.target.value)} placeholder="Paste video URL or upload..." autoFocus style={{flex:1,marginBottom:8}} />
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
            onClick={() => document.getElementById('vid-upload-'+field.name)?.click()}>
            <span style={{fontSize:13,color:'var(--muted2)'}}>▶ Upload video</span>
            <span style={{fontSize:11,color:'var(--muted)'}}>MP4, WebM, MOV up to 50MB</span>
            <input id={'vid-upload-'+field.name} type="file" accept="video/*" style={{display:'none'}}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', { method:'POST', body: formData });
                  const data = await res.json();
                  if (data.url) setVal(data.url);
                } catch(err) { alert('Upload failed'); }
              }} />
          </div>
          {val && <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:8}}>
            <video src={val} controls style={{width:220,height:140,objectFit:'cover',borderRadius:8,display:'block'}} />
            <button className="btn-ghost" style={{fontSize:12,padding:'5px 10px',width:'fit-content'}} onClick={() => setVal('')}>Remove video</button>
          </div>}
        </div>
      )}
      {field.type === 'file' && (
        <div>
          <input className="minput" value={val} onChange={e => setVal(e.target.value)} placeholder="Paste file URL or upload..." autoFocus style={{flex:1,marginBottom:8}} />
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
            onClick={() => document.getElementById('file-upload-'+field.name)?.click()}>
            <span style={{fontSize:13,color:'var(--muted2)'}}>📎 Upload file</span>
            <span style={{fontSize:11,color:'var(--muted)'}}>Any file type, up to 50MB</span>
            <input id={'file-upload-'+field.name} type="file" style={{display:'none'}}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', { method:'POST', body: formData });
                  const data = await res.json();
                  if (data.url) setVal(data.url);
                } catch(err) { alert('Upload failed'); }
              }} />
          </div>
          {val && <div style={{marginTop:10,display:'flex',gap:8,alignItems:'center'}}>
            <div style={{fontSize:12,color:'var(--accent)'}}>✓ {val.split('/').pop()}</div>
            <button className="btn-ghost" style={{fontSize:12,padding:'5px 10px'}} onClick={() => setVal('')}>Remove</button>
          </div>}
        </div>
      )}
      {field.type === 'image' && (
        <div>
          <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
            onClick={() => document.getElementById('img-upload-'+field.name)?.click()}>
            <span style={{fontSize:13,color:'var(--muted2)'}}>⬆ {val ? 'Replace image' : 'Upload image'}</span>
            <span style={{fontSize:11,color:'var(--muted)'}}>JPG, PNG, GIF, WebP</span>
            <input id={'img-upload-'+field.name} type="file" accept="image/*" style={{display:'none'}}
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const formData = new FormData();
                formData.append('image', file);
                try {
                  const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', { method:'POST', body: formData });
                  const data = await res.json();
                  if (data.url) setVal(data.url);
                } catch(err) { alert('Upload failed'); }
              }} />
          </div>
          {val && <div style={{marginTop:10,display:'flex',flexDirection:'column',gap:8}}>
            <img src={val} alt="" style={{width:220,height:150,objectFit:'cover',borderRadius:8,display:'block'}} onError={e => e.target.style.display='none'} />
            <button className="btn-ghost" style={{fontSize:12,padding:'5px 10px',width:'fit-content'}} onClick={() => setVal('')}>Remove image</button>
          </div>}
        </div>
      )}
      {field.type === 'array' && (
        <div className="array-edit">
          {onEditSubFields && (
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:10}}>
              <span style={{fontSize:11,color:'var(--muted)'}}>
                Each item has: {(field.subFields || []).map(sf => sf.label || sf.name).join(', ') || 'no sub-fields yet'}
              </span>
              <button className="edit-schema-btn" style={{flexShrink:0}} onClick={() => onEditSubFields(field)}>
                Edit Fields
              </button>
            </div>
          )}
          <div className="array-items-list">
            {(Array.isArray(val) ? val : []).map((item, i) => (
              <div key={i} className="array-item-row">
                {editingItemIndex === i ? (
                  <div style={{flex:1,display:'flex',flexDirection:'column',gap:8,padding:'8px 0'}}>
                    {(field.subFields||[]).map(sf => (
                      <div key={sf.id}>
                        <label className="mlabel">{sf.label}</label>
                        {sf.type==='array' ? (
                          <NestedArrayFieldEditor value={editingItemVals[sf.name]} onChange={value=>setEditingItemVals(p=>({...p,[sf.name]:value}))} idPrefix={'edit-array-'+sf.name} subFields={sf.subFields || []} />
                        ) : sf.type==='text' ? <textarea className="minput" rows={2} value={editingItemVals[sf.name]||''} onChange={e=>setEditingItemVals(p=>({...p,[sf.name]:e.target.value}))} />
                        : sf.type==='html' ? (
                          <ReactQuill theme="snow" value={editingItemVals[sf.name]||''} onChange={v=>setEditingItemVals(p=>({...p,[sf.name]:v}))} style={{background:'white',borderRadius:6}} />
                        ) : sf.type==='boolean' ? (
                          <select className="minput" value={editingItemVals[sf.name]||'false'} onChange={e=>setEditingItemVals(p=>({...p,[sf.name]:e.target.value==='true'}))}>
                            <option value="true">True</option><option value="false">False</option>
                          </select>
                        ) : sf.type==='image' ? (
                          <div>
                            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                              onClick={() => document.getElementById('edit-img-'+sf.name)?.click()}>
                              <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {editingItemVals[sf.name] ? 'Replace image' : 'Upload image'}</span>
                              <input id={'edit-img-'+sf.name} type="file" accept="image/*" style={{display:'none'}}
                                onChange={async e => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const fd = new FormData();
                                  fd.append('image', file);
                                  try {
                                    const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST', body:fd});
                                    const data = await res.json();
                                    if (data.url) setEditingItemVals(p=>({...p,[sf.name]:data.url}));
                                  } catch(err) { alert('Upload failed'); }
                                }} />
                            </div>
                            {editingItemVals[sf.name] && <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:6}}>
                              <img src={editingItemVals[sf.name]} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6}} onError={e=>e.target.style.display='none'} />
                              <button className="btn-ghost" style={{fontSize:11,padding:'4px 8px',width:'fit-content'}} onClick={() => setEditingItemVals(p=>({...p,[sf.name]:''}))}>Remove image</button>
                            </div>}
                          </div>
                        ) : sf.type==='file' ? (
                          <div>
                            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                              onClick={() => document.getElementById('edit-file-'+sf.name)?.click()}>
                              <span style={{fontSize:12,color:'var(--muted2)'}}>📎 {editingItemVals[sf.name] ? 'Replace file' : 'Upload file'}</span>
                              <input id={'edit-file-'+sf.name} type="file" style={{display:'none'}}
                                onChange={async e => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const fd = new FormData();
                                  fd.append('image', file);
                                  try {
                                    const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST', body:fd});
                                    const data = await res.json();
                                    if (data.url) setEditingItemVals(p=>({...p,[sf.name]:data.url}));
                                  } catch(err) { alert('Upload failed'); }
                                }} />
                            </div>
                            {editingItemVals[sf.name] && <div style={{marginTop:6,display:'flex',gap:6,alignItems:'center'}}>
                              <div style={{fontSize:11,color:'var(--accent)'}}>✓ {editingItemVals[sf.name].split('/').pop()}</div>
                              <button className="btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={() => setEditingItemVals(p=>({...p,[sf.name]:''}))}>Remove</button>
                            </div>}
                          </div>
                        ) : sf.type === 'string' ? (
                          <div>
                            {isSpeakerField(sf)
                              ? <SpeakerNameInput value={editingItemVals[sf.name]} onChange={v=>setEditingItemVals(p=>({...p,[sf.name]:v}))} placeholder={sf.label} />
                              : <input className="minput" type="text" value={editingItemVals[sf.name]||''} onChange={e=>setEditingItemVals(p=>({...p,[sf.name]:e.target.value}))} placeholder={sf.label} />}
                            {console.log(`EditField string ${sf.name}: previewTemplate="${sf.previewTemplate}", value="${editingItemVals[sf.name]}", shouldShow=${!!(sf.previewTemplate && sf.previewTemplate.trim() && editingItemVals[sf.name])}`)}
                            {sf.previewTemplate && sf.previewTemplate.trim() && editingItemVals[sf.name] && (
                              <div style={{marginTop:6,padding:8,background:'var(--bg)',borderRadius:4,fontSize:11,color:'var(--muted2)',border:'1px solid var(--border)'}}>
                                <div style={{fontSize:10,color:'var(--muted)',marginBottom:4}}>Preview:</div>
                                {(() => {
                                  const previewText = sf.previewTemplate + editingItemVals[sf.name];
                                  const isUrl = previewText.startsWith('http://') || previewText.startsWith('https://');
                                  return isUrl ? (
                                    <a href={previewText} target="_blank" rel="noopener noreferrer" style={{fontSize:12,color:'var(--accent)',textDecoration:'underline',wordBreak:'break-all',cursor:'pointer'}}>
                                      {previewText}
                                    </a>
                                  ) : (
                                    <div style={{fontSize:12,color:'var(--text)',wordBreak:'break-all'}}>
                                      {previewText}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        ) : <input className="minput" type={sf.type==='number'?'number':sf.type==='date'?'date':'text'} value={editingItemVals[sf.name]||''} onChange={e=>setEditingItemVals(p=>({...p,[sf.name]:sf.type==='number'?parseFloat(e.target.value)||0:e.target.value}))} placeholder={sf.label} />}
                      </div>
                    ))}
                    <div style={{display:'flex',gap:8,marginTop:4}}>
                      <button className="btn-solid" style={{fontSize:11,padding:'5px 12px'}} onClick={() => {
                        setVal(p => p.map((it,j) => j===i ? {...it,...editingItemVals} : it));
                        setEditingItemIndex(null);
                        setEditingItemVals({});
                      }}>Save Item</button>
                      <button className="btn-ghost" style={{fontSize:11,padding:'5px 12px'}} onClick={() => { setEditingItemIndex(null); setEditingItemVals({}); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="array-item-preview">
                    {typeof item === 'object'
                      ? Object.entries(item).filter(([k]) => k !== '_key').map(([k, v]) => {
                          const isImageUrl = typeof v === 'string' && (v.startsWith('http')||v.includes('.jpg')||v.includes('.png')||v.includes('.webp')||v.includes('/ipfs/'));
                          const isFileUrl = typeof v === 'string' && v.startsWith('http');
                          return (
                            <span key={k} className="array-kv">
                              <span className="array-k">{k}:</span>
                              {isImageUrl
                                ? <img src={v} alt="" style={{width:100,height:100,objectFit:'cover',borderRadius:6,border:'1px solid var(--border2)'}} onError={e=>e.target.style.display='none'} />
                                : isFileUrl
                                ? <span style={{fontSize:11,color:'var(--accent)'}}>📎 {v.split('/').pop()}</span>
                                : <span className="array-v">{String(v).slice(0,30)}{String(v).length>30?'...':''}</span>}
                            </span>
                          );
                        })
                      : <span className="array-v">{String(item)}</span>}
                  </div>
                )}
                {editingItemIndex !== i && (
                  <div style={{display:'flex',gap:4,flexShrink:0}}>
                    <button className="edit-item-btn" onClick={() => { setEditingItemIndex(i); setEditingItemVals({...item}); }} title="Edit">✎</button>
                    <button className="rm-btn" onClick={() => setVal(p => p.filter((_, j) => j !== i))}>×</button>
                  </div>
                )}
              </div>
            ))}
            {(!Array.isArray(val) || val.length === 0) && <div className="array-empty">No items yet.</div>}
          </div>
          <button className="add-item-btn" onClick={() => setAddItemOpen(true)}>+ Add {field.label} Item</button>
          {addItemOpen && (
            <AddArrayItemModal field={field}
              onConfirm={item => { setVal(p => [...(Array.isArray(p)?p:[]), item]); setAddItemOpen(false); }}
              onClose={() => setAddItemOpen(false)} />
          )}
        </div>
      )}
      <div className="mactions">
        {autoSaved && <span style={{fontSize:12,color:'var(--accent)',marginRight:'auto'}}>✓ Saved</span>}
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-solid" onClick={() => { onSave(val); onClose(); }}>Done</button>
      </div>
    </Modal>
  );
}

function EditSingleItemModal({ field, item, onSave, onClose }) {
  const [vals, setVals] = React.useState({ ...item });
  const set = (k, v) => setVals(p => ({ ...p, [k]: v }));

  return (
    <div className="overlay">
      <div className="modal" style={{width:480,maxHeight:'80vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Edit — <span className="accent">{field.label}</span></div>
        {(field.subFields || []).map(sf => (
          <div key={sf.id} style={{marginBottom:14}}>
            <label className="mlabel">{sf.label} <span style={{color:'var(--muted)',fontWeight:400,textTransform:'none'}}>{sf.type}</span></label>
            {sf.type === 'array' ? (
              <NestedArrayFieldEditor value={vals[sf.name]} onChange={value => set(sf.name, value)} idPrefix={'si-array-' + sf.name} subFields={sf.subFields || []} />
            ) : sf.type === 'text' ? (
              <textarea className="minput" rows={3} value={vals[sf.name]||''} onChange={e => set(sf.name, e.target.value)} />
            ) : sf.type === 'html' ? (
              <ReactQuill theme="snow" value={vals[sf.name]||''} onChange={v => set(sf.name, v)} style={{background:'white',borderRadius:6}} />
            ) : sf.type === 'boolean' ? (
              <select className="minput" value={vals[sf.name]||'false'} onChange={e => set(sf.name, e.target.value === 'true')}>
                <option value="true">True</option><option value="false">False</option>
              </select>
            ) : sf.type === 'image' ? (
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                  onClick={() => document.getElementById('si-img-'+sf.name)?.click()}>
                  <span style={{fontSize:12,color:'var(--muted2)'}}>&#8679; {vals[sf.name] ? 'Replace image' : 'Upload image'}</span>
                  <input id={'si-img-'+sf.name} type="file" accept="image/*" style={{display:'none'}}
                    onChange={async e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('image', file);
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST',body:fd});
                        const data = await res.json();
                        if (data.url) set(sf.name, data.url);
                      } catch(err) { alert('Upload failed'); }
                    }} />
                </div>
                {vals[sf.name] && <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:6}}>
                  <img src={vals[sf.name]} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6}} onError={e=>e.target.style.display='none'} />
                  <button className="btn-ghost" style={{fontSize:11,padding:'4px 8px',width:'fit-content'}} onClick={() => set(sf.name, '')}>Remove image</button>
                </div>}
              </div>
            ) : sf.type === 'file' ? (
              <div>
                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 12px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                  onClick={() => document.getElementById('si-file-'+sf.name)?.click()}>
                  <span style={{fontSize:12,color:'var(--muted2)'}}>📎 {vals[sf.name] ? 'Replace file' : 'Upload file'}</span>
                  <input id={'si-file-'+sf.name} type="file" style={{display:'none'}}
                    onChange={async e => {
                      const file = e.target.files?.[0]; if (!file) return;
                      const fd = new FormData(); fd.append('image', file);
                      try {
                        const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST',body:fd});
                        const data = await res.json();
                        if (data.url) set(sf.name, data.url);
                      } catch(err) { alert('Upload failed'); }
                    }} />
                </div>
                {vals[sf.name] && <div style={{marginTop:6,display:'flex',gap:6,alignItems:'center'}}>
                  <div style={{fontSize:11,color:'var(--accent)'}}>✓ {vals[sf.name].split('/').pop()}</div>
                  <button className="btn-ghost" style={{fontSize:11,padding:'4px 8px'}} onClick={() => set(sf.name, '')}>Remove</button>
                </div>}
              </div>
            ) : (
              <>
                {isSpeakerField(sf)
                  ? <SpeakerNameInput value={vals[sf.name]} onChange={v => set(sf.name, v)} placeholder={`Enter ${sf.label}...`} />
                  : <input className="minput" type={sf.type==='number'?'number':sf.type==='date'?'date':'text'}
                  value={vals[sf.name]||''} onChange={e => set(sf.name, sf.type==='number' ? parseFloat(e.target.value)||0 : e.target.value)}
                  placeholder={`Enter ${sf.label}...`} />}
                {sf.type === 'string' && sf.previewTemplate && (
                  <div style={{marginTop:6,padding:6,background:'var(--bg2)',borderRadius:4,fontSize:12,color:'var(--text)',border:'1px solid var(--border)'}}>
                    <div style={{color:'var(--muted)',fontSize:11,marginBottom:4}}>Preview:</div>
                    <div style={{color:'var(--accent)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {(() => {
                        const previewText = (sf.previewTemplate || '') + (vals[sf.name] || '');
                        const isUrl = previewText.startsWith('http://') || previewText.startsWith('https://');
                        return isUrl ? (
                          <a href={previewText} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',textDecoration:'underline',cursor:'pointer'}}>
                            {previewText}
                          </a>
                        ) : previewText;
                      })()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={() => onSave(vals)}>Done</button>
        </div>
      </div>
    </div>
  );
}

function EditSubFieldsModal({ field, docTree, rightPane, onConfirm, onClose }) {
  const SIMPLE_TYPES = ['string','text','number','image','video','boolean','date','html'];
  const [subFields, setSubFields] = React.useState(field.subFields || []);
  const [sfName, setSfName] = React.useState('');
  const [sfType, setSfType] = React.useState('string');
  const [editingSubFieldLabel, setEditingSubFieldLabel] = React.useState(null);
  const [editingLabelValue, setEditingLabelValue] = React.useState('');

  // Capture context values when modal opens
  const contextRef = React.useRef({ docTree, rightPane });
  React.useEffect(() => {
    contextRef.current = { docTree, rightPane };
    console.log('Context captured in modal:', contextRef.current);
  }, [docTree, rightPane]);

  const uid = () => Math.random().toString(36).slice(2, 8);

  const addSubField = () => {
    if (!sfName.trim()) return;
    setSubFields(p => [...p, { id: uid(), name: sfName.trim(), label: sfName.trim(), type: sfType }]);
    setSfName('');
    setSfType('string');
  };

  const handleUpdateSubFieldLabel = (newLabel) => {
    if (!editingSubFieldLabel || !newLabel.trim()) {
      alert('Label cannot be empty');
      return;
    }
    const updated = { ...editingSubFieldLabel, label: newLabel.trim() };
    setSubFields(p => p.map(sf =>
      sf.id === editingSubFieldLabel.id ? updated : sf
    ));
    setEditingSubFieldLabel(null);
  };

  return (
    <div className="overlay" onClick={onClose}>
      <div className="modal" style={{width:480}} onClick={e => e.stopPropagation()}>
        <div className="modal-title">Edit Sub-fields — <span className="accent">{field.label}</span></div>
        <p className="modal-sub">Add or remove sub-fields for this array, or change a sub-field's type from its dropdown.</p>
        <div className="sf-builder">
          <div className="sf-row">
            <input className="minput" style={{flex:1}} value={sfName}
              onChange={e => setSfName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSubField()}
              placeholder="New sub-field name..." autoFocus />
            <select className="mselect" value={sfType} onChange={e => setSfType(e.target.value)}>
              {SIMPLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button className="btn-solid" style={{padding:'9px 16px'}} onClick={addSubField}>+</button>
          </div>
          {subFields.length > 0 && (
            <div className="sf-list">
              {subFields.map((sf, i) => (
                <div key={sf.id} className="sf-item" style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:150}}>
                    <span className="sf-name">{sf.label}</span>
                    {sf.type === 'array' ? <span className="badge">{sf.type}</span> : (
                      <select className="mselect" style={{fontSize:11,padding:'3px 6px',width:'auto'}} value={sf.type} title="Change type"
                        onChange={e => {
                          const type = e.target.value;
                          setSubFields(p => p.map((f, j) => {
                            if (j !== i) return f;
                            const next = { ...f, type };
                            if (type !== 'string') delete next.previewTemplate;
                            return next;
                          }));
                        }}>
                        {(SIMPLE_TYPES.includes(sf.type) ? SIMPLE_TYPES : [...SIMPLE_TYPES, sf.type]).map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    )}
                  </div>
                  {sf.type === 'string' && (
                    <>
                      <button style={{fontSize:11,padding:'4px 10px',whiteSpace:'nowrap',background:sf.previewTemplate?'var(--accent)':'transparent',color:sf.previewTemplate?'white':'var(--text)',border:'1px solid '+(sf.previewTemplate?'var(--accent)':'var(--border)'),borderRadius:'4px',cursor:'pointer'}}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          // Only enable preview if not already enabled
                          if (!sf.previewTemplate || !sf.previewTemplate.trim()) {
                            setSubFields(p => {
                              return p.map((f, j) => {
                                if (j === i) {
                                  const newSf = { ...f };
                                  newSf.previewTemplate = f.label ? (f.label + ': ') : 'Item: ';
                                  return newSf;
                                }
                                return f;
                              });
                            });
                          }
                        }}>
                        {sf.previewTemplate && sf.previewTemplate.trim() ? '✓ Preview' : '+ Preview'}
                      </button>
                      {sf.previewTemplate && (
                        <>
                          <input type="text" style={{fontSize:10,padding:'3px 6px',border:'1px solid var(--border)',borderRadius:'3px',flex:1,minWidth:80,maxWidth:150}}
                            value={sf.previewTemplate}
                            onChange={e => {
                              const newSf = { ...sf, previewTemplate: e.target.value };
                              setSubFields(p => {
                                return p.map((f, j) => j === i ? newSf : f);
                              });
                            }}
                            placeholder="Template..." />
                          <button style={{fontSize:10,padding:'2px 6px',minWidth:'30px',color:'var(--muted)'}} className="btn-ghost" onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSubFields(p => {
                              return p.map((f, j) => {
                                if (j === i) {
                                  const newSf = { ...f };
                                  newSf.previewTemplate = '';
                                  return newSf;
                                }
                                return f;
                              });
                            });
                          }}>✕</button>
                        </>
                      )}
                    </>
                  )}
                  <button className="edit-label-btn" onClick={() => {setEditingSubFieldLabel(sf); setEditingLabelValue(sf.label);}} title="Edit label">✎</button>
                  <button className="rm-btn" onClick={() => setSubFields(p => p.filter((_,j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="mactions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-solid" onClick={() => {
            console.log('=== SAVE SUB-FIELDS BUTTON CLICKED ===');
            console.log('field.id:', field.id);
            console.log('subFields:', subFields);
            console.log('Using captured context:', contextRef.current);
            onConfirm(field.id, subFields, contextRef.current.docTree, contextRef.current.rightPane);
          }} disabled={subFields.length === 0}>
            Save Sub-fields
          </button>
        </div>

        {editingSubFieldLabel && (
          <Modal onClose={() => setEditingSubFieldLabel(null)}>
            <div className="modal-title">Edit Sub-field Label</div>
            <label className="mlabel">Display Name</label>
            <input className="minput" value={editingLabelValue} onChange={e => setEditingLabelValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleUpdateSubFieldLabel(editingLabelValue)}
              placeholder="Enter sub-field display name" autoFocus />
            <div style={{fontSize:11,color:'var(--muted)',marginTop:8}}>
              Field key "{editingSubFieldLabel.name}" will remain unchanged
            </div>
            <div className="mactions">
              <button className="btn-ghost" onClick={() => setEditingSubFieldLabel(null)}>Cancel</button>
              <button className="btn-solid" onClick={() => handleUpdateSubFieldLabel(editingLabelValue)}>Update</button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

export function SI3TableEditor({ table, onAddRow, onUpdateRow, onDeleteRow, onAddColumn, onUpdateColumn, onDeleteColumn, userRole, rowLabel = 'Row', extraActions, heading, listNames = [], selectedLists = [], onReviewRow, onMarkPaid }) {
  const [newRow, setNewRow] = useState({});
  const [editingRowId, setEditingRowId] = useState(null);
  const [addingNewRow, setAddingNewRow] = useState(false);
  const [addingColumn, setAddingColumn] = useState(false);
  const [columnName, setColumnName] = useState('');
  const [columnLists, setColumnLists] = useState([]);
  const [tableTab, setTableTab] = useState('data');
  const [editingColumn, setEditingColumn] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [editColumnName, setEditColumnName] = useState('');
  const [editColumnLists, setEditColumnLists] = useState([]);
  const [copiedCell, setCopiedCell] = useState('');
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState([]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchText.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setSearchText('');
    setDebouncedSearch('');
    setFilters([]);
  }, [table?._id]);

  const displayColumns = table?.columns || [];
  const tableRows = table?.rows || [];
  const showDateAdded = Array.isArray(listNames) && listNames.length > 0;

  const filteredRows = React.useMemo(() => {
    const stringValue = (value) => {
      if (value == null) return '';
      if (Array.isArray(value)) return value.map(stringValue).filter(item => String(item).trim() !== '').join(' ');
      if (typeof value === 'object') {
        try { return JSON.stringify(value); } catch (e) { return String(value); }
      }
      return String(value);
    };
    const search = debouncedSearch.toLowerCase();
    return tableRows.filter((row) => {
      if (search) {
        const haystack = Object.values(row.data || {})
          .map(stringValue)
          .filter(value => String(value).trim() !== '')
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      for (const filter of filters) {
        if (!filter.columnId) continue;
        const column = displayColumns.find(col => col.id === filter.columnId);
        if (!column) continue;
        const value = stringValue(row.data?.[column.id] ?? row.data?.[column.name]).trim();
        const needle = String(filter.value || '').trim();
        if (filter.operator === 'empty') {
          if (value !== '') return false;
        } else if (filter.operator === 'notEmpty') {
          if (value === '') return false;
        } else if (filter.operator === 'is') {
          if (value.toLowerCase() !== needle.toLowerCase()) return false;
        } else if (!value.toLowerCase().includes(needle.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  }, [tableRows, debouncedSearch, filters, displayColumns]);

  if (!table) return null;
  const fmtDateAdded = (v) => {
    const d = v ? new Date(v) : null;
    return d && !isNaN(d) ? d.toISOString().slice(0, 10) : '—';
  };
  const stickyIdentityIndex = (col) => {
    const key = String(col?.name || '').trim().toLowerCase();
    if (key === 'name' || col?.id === 'col-name') return 0;
    if (key === 'email' || col?.id === 'col-email') return 1;
    if (key === 'lists' || col?.id === 'col-lists') return 2;
    return -1;
  };
  const listsCol = (table.columns || []).find((c) => c.id === 'col-lists' || String(c.name || '').trim().toLowerCase() === 'lists');
  // One entry per application this row still awaits a decision on. Someone
  // tagged for both DeFi and DAO gets a button pair for each, so approving one
  // no longer also actions - and emails about - the other.
  const applicationKey = (tag) => {
    const k = String(tag || '').toLowerCase();
    if (k.includes('defi')) return 'defi';
    if (k.includes('dao') || k.includes('guide')) return 'dao';
    if (k.includes('grow3dge')) return 'grow3dge';
    return '';
  };
  const [reviewingId, setReviewingId] = useState(null);
  const [reviewingAction, setReviewingAction] = useState(null);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const rowReview = (row) => {
    if (!onReviewRow || !listsCol) return null;
    const rawVal = row?.data?.[listsCol.id] || row?.data?.[listsCol.name] || row?.data?.Lists;
    const tags = String(rawVal || '').split(',').map((part) => part.trim()).filter(Boolean);
    const pending = [];
    for (const tag of tags) {
      const key = applicationKey(tag);
      if (!key || pending.some((item) => item.key === key)) continue;
      const approved = tags.some((n) => applicationKey(n) === key && /\bapproved\b/i.test(n));
      const denied = tags.some((n) => applicationKey(n) === key && /\bdenied\b/i.test(n));
      const applied = tags.some((n) => applicationKey(n) === key && /\b(applied|applicant|applicants|application)\b/i.test(n));
      if (!applied && !approved && !denied) continue;
      if (approved) continue;
      pending.push({
        key,
        label: key === 'defi' ? 'Si Her DeFi' : key === 'grow3dge' ? 'Grow3dge' : 'Si Her DAO',
        short: key === 'defi' ? 'DeFi' : key === 'grow3dge' ? 'Grow3dge' : 'DAO',
        approved,
        denied
      });
    }
    return pending.length ? pending : null;
  };
  const GROW3DGE_APPROVED_TAG = 'grow3dge accelerator season 3 approved';
  const rowIsGrow3dgeApproved = (row) => {
    if (!listsCol) return false;
    const rawVal = row?.data?.[listsCol.id] || row?.data?.[listsCol.name] || row?.data?.Lists;
    return String(rawVal || '')
      .split(',')
      .map((part) => part.trim().toLowerCase())
      .includes(GROW3DGE_APPROVED_TAG);
  };
  const rowIsPaid = (row) => {
    const direct = row?.data?.['Paid At'];
    if (direct != null && String(direct).trim() !== '') return true;
    const paidCol = displayColumns.find((c) => String(c.name || '').trim().toLowerCase() === 'paid at');
    const value = paidCol ? (row?.data?.[paidCol.id] ?? row?.data?.[paidCol.name]) : null;
    return value != null && String(value).trim() !== '';
  };

  const handleAddNewRow = () => {
    if (Object.keys(newRow).length === 0) {
      alert('Please fill in at least one column');
      return;
    }
    onAddRow(newRow);
    setNewRow({});
    setAddingNewRow(false);
  };

  const handleConfirmAddColumn = () => {
    if (!columnName.trim()) {
      alert('Please enter a column name');
      return;
    }
    const colId = 'col-' + uid();
    const column = { id: colId, name: columnName.trim(), type: 'string', required: false, defaultValue: null };
    if (columnLists.length) column.visibleOnLists = columnLists;
    onAddColumn(column);
    setColumnName('');
    setColumnLists([]);
    setAddingColumn(false);
  };

  const handleConfirmEditColumn = () => {
    if (!editingColumn || !editColumnName.trim() || !onUpdateColumn) return;
    const payload = { name: editColumnName.trim() };
    if (listNames.length) payload.visibleOnLists = editColumnLists;
    onUpdateColumn(editingColumn.id, payload);
    setEditingColumn(null);
  };

  const toggleList = (name, current, setter) => {
    const on = current.some((item) => crmNameKey(item) === crmNameKey(name));
    setter(on ? current.filter((item) => crmNameKey(item) !== crmNameKey(name)) : [...current, name]);
  };

  const hasActiveFilters = Boolean(searchText.trim()) || filters.length > 0;
  const addFilter = () => setFilters(current => [...current, { id: 'filter-' + uid(), columnId: displayColumns[0]?.id || '', operator: 'contains', value: '' }]);
  const updateFilter = (id, patch) => setFilters(current => current.map(item => item.id === id ? { ...item, ...patch } : item));
  const removeFilter = (id) => setFilters(current => current.filter(item => item.id !== id));
  const clearAllFilters = () => {
    setSearchText('');
    setDebouncedSearch('');
    setFilters([]);
  };

  return (
    <div className="si3table-editor">
      <div className="table-header">
        <div>
          <h2>{heading || table.name}</h2>
          {table.description && <p className="table-desc">{table.description}</p>}
        </div>
        {userRole !== 'viewer' && tableTab === 'data' && (
          <div style={{display:'flex',gap:8,flexShrink:0,flexWrap:'wrap',justifyContent:'flex-end'}}>
            {extraActions}
            <button className="btn-add-col" onClick={() => { setColumnLists(selectedLists || []); setAddingColumn(true); }}>+ Add Column</button>
          </div>
        )}
      </div>

      <div className="tabs" style={{paddingLeft:20,borderBottom:'1px solid var(--border)',marginBottom:0}}>
        <button className={`tab ${tableTab === 'data' ? 'act' : ''}`} onClick={() => setTableTab('data')}>Data</button>
        <button className={`tab ${tableTab === 'api' ? 'act' : ''}`} onClick={() => setTableTab('api')}>API</button>
      </div>

      {addingColumn && (
        <Modal onClose={() => { setAddingColumn(false); setColumnName(''); setColumnLists([]); }}>
          <div className="modal-title">Add Column</div>
          <label className="mlabel">Column Name</label>
          <input className="minput"
            value={columnName}
            onChange={e => setColumnName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleConfirmAddColumn()}
            placeholder="e.g. Email, Age, Status..."
            autoFocus />
          {listNames.length > 0 && (
            <>
              <label className="mlabel" style={{marginTop:12}}>Show on lists</label>
              <div style={{fontSize:11,color:'var(--muted)',marginBottom:8}}>Leave empty to show on All Members and every list.</div>
              <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:180,overflow:'auto',marginBottom:12}}>
                {listNames.map((name) => (
                  <label key={name} style={{display:'flex',gap:8,alignItems:'center',fontSize:13}}>
                    <input type="checkbox" checked={columnLists.some((item) => crmNameKey(item) === crmNameKey(name))} onChange={() => toggleList(name, columnLists, setColumnLists)} />
                    {name}
                  </label>
                ))}
              </div>
            </>
          )}
          <div className="mactions">
            <button className="btn-ghost" onClick={() => { setAddingColumn(false); setColumnName(''); setColumnLists([]); }}>Cancel</button>
            <button className="btn-solid" onClick={handleConfirmAddColumn}>Add Column</button>
          </div>
        </Modal>
      )}

      {editingColumn && (
        <Modal onClose={() => setEditingColumn(null)}>
          <div className="modal-title">Edit field</div>
          <label className="mlabel">Field name</label>
          <input className="minput"
            value={editColumnName}
            onChange={e => setEditColumnName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleConfirmEditColumn()}
            autoFocus />
          {listNames.length > 0 && (
            <>
              <label className="mlabel" style={{marginTop:12}}>Show on lists</label>
              <div style={{fontSize:11,color:'var(--muted)',marginBottom:8}}>Leave empty to show on All Members and every list.</div>
              <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:180,overflow:'auto',marginBottom:12}}>
                {listNames.map((name) => (
                  <label key={name} style={{display:'flex',gap:8,alignItems:'center',fontSize:13}}>
                    <input type="checkbox" checked={editColumnLists.some((item) => crmNameKey(item) === crmNameKey(name))} onChange={() => toggleList(name, editColumnLists, setEditColumnLists)} />
                    {name}
                  </label>
                ))}
              </div>
            </>
          )}
          <div className="mactions">
            <button className="btn-ghost" onClick={() => setEditingColumn(null)}>Cancel</button>
            <button className="btn-solid" onClick={handleConfirmEditColumn}>Save</button>
          </div>
        </Modal>
      )}

      {confirmState && (
        <ConfirmDialog
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          busyLabel={confirmState.busyLabel}
          busy={confirming}
          onCancel={() => { if (!confirming) setConfirmState(null); }}
          onConfirm={async () => {
            if (confirming) return;
            setConfirming(true);
            try {
              await confirmState.onConfirm();
            } finally {
              setConfirming(false);
              setConfirmState(null);
            }
          }}
        />
      )}

      {tableTab === 'data' && (
        <>
          <div className="table-tools">
            <div className="table-tools-row">
              <div className="table-search">
                <input
                  className="table-input"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder="Search name, email or any field"
                  aria-label="Search name, email or any field"
                />
                {searchText && (
                  <button type="button" className="table-search-clear" onClick={() => { setSearchText(''); setDebouncedSearch(''); }} aria-label="Clear search">×</button>
                )}
              </div>
              <button type="button" className="btn-ghost" onClick={addFilter}>+ Add filter</button>
              {hasActiveFilters && (
                <button type="button" className="btn-ghost" onClick={clearAllFilters}>Clear all</button>
              )}
              {hasActiveFilters && (
                <span className="table-count">Showing {filteredRows.length} of {tableRows.length}</span>
              )}
            </div>
            {filters.map((filter) => {
              const noValue = filter.operator === 'empty' || filter.operator === 'notEmpty';
              return (
                <div className="table-tools-row" key={filter.id}>
                  <select
                    className="table-input table-filter-select"
                    value={filter.columnId}
                    onChange={e => updateFilter(filter.id, { columnId: e.target.value })}
                    aria-label="Filter column"
                  >
                    {displayColumns.map(col => (
                      <option key={col.id} value={col.id}>{col.name}</option>
                    ))}
                  </select>
                  <select
                    className="table-input table-filter-select"
                    value={filter.operator}
                    onChange={e => updateFilter(filter.id, { operator: e.target.value })}
                    aria-label="Filter operator"
                  >
                    <option value="contains">contains</option>
                    <option value="is">is</option>
                    <option value="empty">is empty</option>
                    <option value="notEmpty">is not empty</option>
                  </select>
                  {!noValue && (
                    <input
                      className="table-input table-filter-value"
                      value={filter.value}
                      onChange={e => updateFilter(filter.id, { value: e.target.value })}
                      placeholder="Value"
                      aria-label="Filter value"
                    />
                  )}
                  <button type="button" className="ico-btn" onClick={() => removeFilter(filter.id)} title="Remove filter" aria-label="Remove filter">×</button>
                </div>
              );
            })}
          </div>
          <div className="table-grid">
            <table>
              <thead>
                <tr>
                  {(displayColumns).map(col => (
                    <th
                      key={col.id}
                      className={stickyIdentityIndex(col) >= 0 ? `sticky-id sticky-id-${stickyIdentityIndex(col)}` : undefined}
                    >
                      <div className="th-content">
                        <span>{col.name}</span>
                        {userRole !== 'viewer' && (
                          <div className="col-actions">
                            {onUpdateColumn && (
                              <button className="ico-btn" onClick={() => {
                                setEditingColumn(col);
                                setEditColumnName(col.name);
                                setEditColumnLists(Array.isArray(col.visibleOnLists) ? col.visibleOnLists : []);
                              }} title="Rename field">✎</button>
                            )}
                            <button className="ico-btn" onClick={() => {
                              setConfirmState({
                                title: 'Delete field',
                                message: `Delete the "${col.name}" field? Its values will be removed from every row.`,
                                confirmLabel: 'Delete field',
                                onConfirm: () => onDeleteColumn(col.id)
                              });
                            }} title="Delete column">×</button>
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                  {showDateAdded && <th>Date Added</th>}
                  {userRole !== 'viewer' && <th className="th-actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => (
                  <tr key={row._id || row.rowId}>
                    {(displayColumns).map(col => {
                      const cellText = String(
                        row.data?.[col.id]
                        ?? (Array.isArray(col.lookupKeys) ? col.lookupKeys.map((key) => row.data?.[key]).find((value) => value != null && String(value).trim() !== '') : '')
                        ?? ''
                      ).trim();
                      const cellKey = `${row._id || row.rowId}:${col.id}`;
                      return (
                      <td
                        key={col.id}
                        className={stickyIdentityIndex(col) >= 0 ? `sticky-id sticky-id-${stickyIdentityIndex(col)}` : undefined}
                      >
                        {editingRowId === (row._id || row.rowId) ? (
                          <input
                            type={col.type === 'number' ? 'number' : 'text'}
                            value={newRow[col.id] ?? ''}
                            onChange={e => setNewRow({ ...newRow, [col.id]: e.target.value })}
                            className="table-input"
                          />
                        ) : (
                          <span
                            className={`table-cell-text${cellText ? ' copyable' : ''}`}
                            title={cellText ? (copiedCell === cellKey ? 'Copied' : 'Click to copy') : ''}
                            onClick={() => {
                              if (!cellText || !navigator.clipboard) return;
                              navigator.clipboard.writeText(cellText).then(() => {
                                setCopiedCell(cellKey);
                                window.setTimeout(() => setCopiedCell((cur) => cur === cellKey ? '' : cur), 1200);
                              }).catch(() => {});
                            }}
                          >{copiedCell === cellKey ? 'Copied' : (cellText || '—')}</span>
                        )}
                      </td>
                      );
                    })}
                    {showDateAdded && (
                      <td><span className="table-cell-text">{fmtDateAdded(row.createdAt)}</span></td>
                    )}
                    {userRole !== 'viewer' && (
                      <td className="td-actions">
                        {editingRowId === (row._id || row.rowId) ? (
                          <>
                            <button className="ico-btn" onClick={() => {
                              onUpdateRow(row._id || row.rowId, newRow);
                              setEditingRowId(null);
                            }}>✓</button>
                            <button className="ico-btn" onClick={() => setEditingRowId(null)}>✕</button>
                          </>
                        ) : (
                          <>
                            <button className="ico-btn" onClick={() => {
                              setNewRow(row.data || {});
                              setEditingRowId(row._id || row.rowId);
                            }}>✎</button>
                            {(rowReview(row) || []).map((app) => {
                              const id = row.rowId || row._id;
                              const rowReviewing = Boolean(reviewingId && reviewingId.startsWith(`${id}-`));
                              const approveReviewing = reviewingAction === 'approve' && reviewingId === `${id}-${app.key}`;
                              const denyReviewing = reviewingAction === 'deny' && reviewingId === `${id}-${app.key}`;
                              const ineligibleReviewing = reviewingAction === 'ineligible' && reviewingId === `${id}-${app.key}-ineligible`;
                              return (
                                <React.Fragment key={app.key}>
                                  {!app.approved && (
                                    <button
                                      className="btn-solid"
                                      title={`Approve ${app.label} application`}
                                      style={{ fontSize: 12, padding: '3px 8px', opacity: rowReviewing ? 0.6 : 1 }}
                                      disabled={rowReviewing}
                                      onClick={async () => {
                                        setReviewingId(`${id}-${app.key}`);
                                        setReviewingAction('approve');
                                        try {
                                          await onReviewRow(id, 'approve', app);
                                        } finally {
                                          setReviewingId(null);
                                          setReviewingAction(null);
                                        }
                                      }}
                                    >{approveReviewing ? 'Approving...' : `✓ ${app.short}`}</button>
                                  )}
                                  {!app.denied && (
                                    <button
                                      className="btn-ghost"
                                      title={`Deny ${app.label} application`}
                                      style={{ fontSize: 12, padding: '3px 8px', opacity: rowReviewing ? 0.6 : 1 }}
                                      disabled={rowReviewing}
                                      onClick={async () => {
                                        setReviewingId(`${id}-${app.key}`);
                                        setReviewingAction('deny');
                                        try {
                                          await onReviewRow(id, 'deny', app);
                                        } finally {
                                          setReviewingId(null);
                                          setReviewingAction(null);
                                        }
                                      }}
                                    >{denyReviewing ? 'Denying...' : `✕ ${app.short}`}</button>
                                  )}
                                  {app.key === 'grow3dge' && !app.denied && (
                                    <button
                                      className="btn-ghost"
                                      title={`Mark ${app.label} application as not eligible`}
                                      style={{ fontSize: 12, padding: '3px 8px', opacity: rowReviewing ? 0.6 : 1 }}
                                      disabled={rowReviewing}
                                      onClick={async () => {
                                        setReviewingId(`${id}-${app.key}-ineligible`);
                                        setReviewingAction('ineligible');
                                        try {
                                          await onReviewRow(id, 'ineligible', app);
                                        } finally {
                                          setReviewingId(null);
                                          setReviewingAction(null);
                                        }
                                      }}
                                    >{ineligibleReviewing ? 'Marking...' : 'Not eligible'}</button>
                                  )}
                                </React.Fragment>
                              );
                            })}
                            {onMarkPaid && rowIsGrow3dgeApproved(row) && !rowIsPaid(row) && (
                              <button
                                className="btn-solid"
                                title="Mark this applicant as paid"
                                style={{ fontSize: 12, padding: '3px 8px', opacity: markingPaidId ? 0.6 : 1 }}
                                disabled={Boolean(markingPaidId)}
                                onClick={async () => {
                                  const id = row.rowId || row._id;
                                  setMarkingPaidId(id);
                                  try {
                                    await onMarkPaid(id);
                                  } finally {
                                    setMarkingPaidId(null);
                                  }
                                }}
                              >{markingPaidId === (row.rowId || row._id) ? 'Marking paid...' : 'Mark paid'}</button>
                            )}
                            {onMarkPaid && rowIsGrow3dgeApproved(row) && rowIsPaid(row) && (
                              <span className="badge" title="Onboarding email sent">Paid</span>
                            )}
                            <button
                              className="ico-btn"
                              title="Delete this row"
                              onClick={() => {
                                const findColumn = (id, name) => displayColumns.find(c =>
                                  String(c.id || '').toLowerCase() === id ||
                                  String(c.name || '').trim().toLowerCase() === name
                                );
                                const valueFor = (column) => {
                                  if (!column) return '';
                                  const value = row.data?.[column.id] ?? row.data?.[column.name];
                                  return value == null ? '' : String(value).trim();
                                };
                                const name = valueFor(findColumn('col-name', 'name'));
                                const email = valueFor(findColumn('col-email', 'email'));
                                const label = name && email ? `${name} (${email})` : (name || email);
                                setConfirmState({
                                  title: 'Delete row',
                                  message: label ? `Delete "${label}"? This cannot be undone.` : 'Delete this row? This cannot be undone.',
                                  confirmLabel: 'Delete row',
                                  busyLabel: 'Deleting...',
                                  onConfirm: () => onDeleteRow(row.rowId || row._id)
                                });
                              }}
                            >Delete</button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {userRole !== 'viewer' && (
            <div className="table-footer">
              {addingNewRow ? (
                <div style={{display:'flex',gap:8}}>
                  <button className="btn-solid" onClick={handleAddNewRow}>Save {rowLabel}</button>
                  <button className="btn-ghost" onClick={() => { setNewRow({}); setAddingNewRow(false); }}>Cancel</button>
                </div>
              ) : (
                <button className="btn-solid" onClick={() => setAddingNewRow(true)}>+ Add {rowLabel}</button>
              )}
            </div>
          )}

          {addingNewRow && (
            <div className="table-grid" style={{marginTop:16,borderTop:'1px solid var(--border)',paddingTop:16}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--muted)',marginBottom:12}}>New {rowLabel}</div>
              <div style={{display:'flex',flexDirection:'column',gap:12}}>
                {(displayColumns).map(col => (
                  <div key={col.id} style={{display:'flex',flexDirection:'column',gap:4}}>
                    <label style={{fontSize:11,fontWeight:500,color:'var(--muted2)'}}>{col.name} {col.required && <span style={{color:'var(--red)'}}>*</span>}</label>
                    <input
                      type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'}
                      value={newRow[col.id] || ''}
                      onChange={e => setNewRow({...newRow, [col.id]: col.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value})}
                      className="table-input"
                      placeholder={`Enter ${col.name}...`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tableTab === 'api' && (
        <div className="editor-body">
          <div className="api-block">
            <div className="api-label">Get Table</div>
            <pre className="api-code">{`GET ${process.env.REACT_APP_API_URL}/api/si3tables/${table._id}`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Add Row (Using Column Names - Recommended)</div>
            <pre className="api-code">{'POST ' + (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/si3tables/' + table._id + '/rows\n' +
`{
  "rowId": "unique-row-id",
  "data": {
${(table.columns || []).map(col => `    "${col.name}": "value"`).join(',\n')}
  }
}`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Add Row (Using Column IDs - Also Supported)</div>
            <pre className="api-code">{'POST ' + (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/si3tables/' + table._id + '/rows\n' +
`{
  "rowId": "unique-row-id",
  "data": {
${(table.columns || []).map(col => `    "${col.id}": "value"`).join(',\n')}
  }
}`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Update Row (Using Column Names - Recommended)</div>
            <pre className="api-code">{'PUT ' + (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/si3tables/' + table._id + '/rows/:rowId\n' +
`{
  "data": {
${(table.columns || []).map(col => `    "${col.name}": "updated-value"`).join(',\n')}
  }
}`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Update Row (Using Column IDs - Also Supported)</div>
            <pre className="api-code">{'PUT ' + (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/si3tables/' + table._id + '/rows/:rowId\n' +
`{
  "data": {
${(table.columns || []).map(col => `    "${col.id}": "updated-value"`).join(',\n')}
  }
}`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Delete Row</div>
            <pre className="api-code">{`DELETE ${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/si3tables/${table._id}/rows/:rowId`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Add Column</div>
            <pre className="api-code">{'POST ' + (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/si3tables/' + table._id + '/columns\n' +
`{
  "id": "col-new",
  "name": "Column Name",
  "type": "string",
  "required": false,
  "defaultValue": null
}`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Delete Column</div>
            <pre className="api-code">{`DELETE ${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/si3tables/${table._id}/columns/:columnId`}</pre>
          </div>

          <div className="api-block">
            <div className="api-label">Table Info</div>
            <pre className="api-code">{`// Table ID: ${table._id}
// Table Slug: ${table.slug}
// Columns: ${(table.columns || []).length}
// Rows: ${table.rowCount || 0}

// Available column types: string, number, text, boolean, date, email, url, image, file`}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_CRM_LISTS = [
  'Grow3dge Members',
  'Grow3dge Incubator Members',
  'Si Her DAO Members',
  'Si Her Circles Members',
  'Si Her DeFi Applied',
  'Si Her DeFi Approved',
  'Si Her DeFi Denied',
  'Si Her DeFi Members',
  'Si Her DAO Applied',
  'Si Her DAO Approved',
  'Si Her DAO Denied',
  'Grow3dge Inquirers',
  'Well-Being Inquirers',
  'Well-Being Members'
];
const crmNameKey = (name) => String(name || '').trim().toLowerCase();
const toTitleCaseLabel = (value) => String(value || '').trim().split(/\s+/).filter(Boolean).map((word) =>
  word.split('-').map((part) => {
    const bare = part.replace(/[^a-zA-Z0-9]/g, '');
    const key = bare.toLowerCase();
    if (key === 'dao') return part.replace(bare, 'DAO');
    if (key === 'defi') return part.replace(bare, 'DeFi');
    if (key === 'tg') return part.replace(bare, 'TG');
    if (key === 'seo') return part.replace(bare, 'SEO');
    if (!part) return part;
    return part.charAt(0).toUpperCase() + part.slice(1);
  }).join('-')
).join(' ');
const PAGE_SECTION_ORDER = ['SI3 Pages', 'NavBar', 'SEO', 'DECKS', 'Certifications'];
const PAGE_SLUG_ORDER = [
  'homepage', 'grow3dge', 'si-her-dao', 'wellbeing', 'brandkit', 'policy',
  'navbar', 'seo-footer', 'presentation-decks',
  'si-her-defi-placeholder', 'incubator-avalanche-placeholder', 'si-her-defi-gnosis'
];
const sortPagesByStructure = (list) => [...(list || [])].sort((a, b) => {
  const ia = PAGE_SLUG_ORDER.indexOf(a.slug);
  const ib = PAGE_SLUG_ORDER.indexOf(b.slug);
  return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
});
const isCrmTable = (t) => {
  if (!t) return false;
  if (t.section === 'crm') return true;
  const key = crmNameKey(t.name);
  if (key === 'common' || key === 'members') return true;
  return DEFAULT_CRM_LISTS.some(g => crmNameKey(g) === key) || key === 'grow3dge';
};
const crmListsColumn = (table) =>
  (table?.columns || []).find((c) => c.id === 'col-lists' || crmNameKey(c.name) === 'lists') || null;
const parseCrmListTags = (value) => String(value || '')
  .split(',')
  .map((part) => part.trim())
  .filter(Boolean);
const formatCrmListTags = (names) => {
  const seen = new Set();
  const out = [];
  (names || []).forEach((name) => {
    const trimmed = String(name || '').trim();
    const key = crmNameKey(trimmed);
    if (!trimmed || seen.has(key)) return;
    seen.add(key);
    out.push(trimmed);
  });
  return out.join(', ');
};
const listMatchesKey = (a, b) => {
  if (a === b) return true;
  const norm = (k) => String(k || '').toLowerCase().replace(/\b(dao|guide)\b/g, 'dao_guide').replace(/[^a-z0-9]+/g, ' ').trim();
  return norm(a) === norm(b);
};
const rowListTags = (table, row) => {
  const col = crmListsColumn(table);
  const raw = col ? (row?.data?.[col.id] || row?.data?.[col.name] || row?.data?.Lists) : row?.data?.Lists;
  return parseCrmListTags(raw);
};
const rowInCrmLists = (table, row, selected) => {
  if (!selected?.length) return true;
  const tags = rowListTags(table, row).map(crmNameKey);
  return selected.some((selName) => {
    const selKey = crmNameKey(selName);
    return tags.some((tagKey) => listMatchesKey(tagKey, selKey));
  });
};
const crmListNames = (table) => {
  const fromTable = Array.isArray(table?.crmLists) ? table.crmLists : [];
  const fromRows = (table?.rows || []).flatMap((row) => rowListTags(table, row));
  const seen = new Set();
  const out = [];
  const add = (name) => {
    const trimmed = String(name || '').trim();
    const key = crmNameKey(trimmed);
    if (!trimmed || key === 'common' || seen.has(key)) return;
    seen.add(key);
    out.push(trimmed);
  };
  fromTable.forEach(add);
  fromRows.forEach(add);
  return out;
};
const countCrmList = (table, listName) =>
  (table?.rows || []).filter((row) => rowInCrmLists(table, row, [listName])).length;
const isCirclesList = (name) => crmNameKey(name).includes('circles');
const formForCrmList = (listName, formList) => {
  const key = crmNameKey(listName);
  return (formList || []).find((form) => {
    const hay = `${form?.title || ''} ${form?.slug || ''}`.toLowerCase();
    if (hay.includes('speaker')) return false;
    if (key.includes('defi') && hay.includes('defi')) return true;
    if (key.includes('dao') && hay.includes('dao') && !hay.includes('defi')) return true;
    if (key.includes('grow3dge') && hay.includes('grow3dge')) return true;
    if ((key.includes('well') || key.includes('wellbeing')) && (hay.includes('wellbeing') || hay.includes('well-being') || hay.includes('well being'))) return true;
    return false;
  }) || null;
};
const crmColForFormField = (columns, field) => {
  const pool = columns || [];
  const hit = (pred) => pool.find(pred);
  if (!field) return null;
  const compact = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const fieldCompacts = [field.id, field.name, field.label].map(compact).filter((value) => value.length > 2);
  let col = hit((c) => c.id === field.id);
  if (col) return col;
  col = hit((c) => c.id === field.name || crmNameKey(c.id) === crmNameKey(field.name));
  if (col) return col;
  col = hit((c) => crmNameKey(c.name) === crmNameKey(field.name) || crmNameKey(c.name) === crmNameKey(field.label));
  if (col) return col;
  col = hit((c) => {
    const colCompact = compact(c.name) || compact(c.id);
    if (!colCompact) return false;
    return fieldCompacts.some((item) => (
      item === colCompact ||
      (item.length > 18 && colCompact.length > 18 && (item.startsWith(colCompact) || colCompact.startsWith(item))) ||
      (item.includes('siherfund') && colCompact.includes('siherfund')) ||
      (item.includes('fundinginterest') && (colCompact.includes('fund') || colCompact.includes('funding'))) ||
      ((item.includes('somatic') || item.includes('zuber')) && (colCompact.includes('somatic') || colCompact.includes('zuber') || colCompact.includes('coaching')))
    ));
  });
  if (col) return col;
  if (field.type === 'email' || /email/i.test(field.name || '') || /email/i.test(field.label || '')) {
    return hit((c) => c.id === 'col-email' || crmNameKey(c.name) === 'email');
  }
  const nameKeyValue = crmNameKey(field.name || field.label || '');
  if (nameKeyValue === 'name' || nameKeyValue === 'your name' || nameKeyValue === 'full name' || nameKeyValue === 'your_name') {
    return hit((c) => crmNameKey(c.name) === 'your name' || crmNameKey(c.name) === 'name' || c.id === 'col-name');
  }
  return {
    id: field.id || field.name,
    name: field.label || field.name,
    _virtual: true,
    lookupKeys: [field.id, field.name, field.label]
  };
};
const columnVisibleOnLists = (col, selectedLists) => {
  if (!selectedLists?.length) return true;
  const vis = Array.isArray(col?.visibleOnLists) ? col.visibleOnLists : [];
  if (!vis.length) return true;
  const selected = selectedLists.map(crmNameKey);
  return vis.some((name) => selected.includes(crmNameKey(name)));
};
const columnsForCrmView = (columns, selectedLists, formList = []) => {
  const pool = columns || [];
  const identity = [];
  const used = new Set();
  ['name', 'email', 'lists'].forEach((key) => {
    const col = pool.find((c) => crmNameKey(c.name) === key || c.id === `col-${key}`);
    if (col && !used.has(col.id)) {
      used.add(col.id);
      identity.push(col);
    }
  });
  if (!selectedLists?.length) return identity;
  const out = identity.slice();
  selectedLists.forEach((listName) => {
    const form = formForCrmList(listName, formList);
    (form?.fields || []).forEach((field) => {
      const col = crmColForFormField(pool, field);
      if (!col || used.has(col.id)) return;
      used.add(col.id);
      out.push({ ...col, name: field.label || col.name });
    });
  });
  if (selectedLists.some((name) => formForCrmList(name, formList))) return out;
  return identity.concat(pool.filter((col) => !used.has(col.id) && columnVisibleOnLists(col, selectedLists)));
};

export default function App({ user, userRole = "admin", project, onBack }) {
  const [pages, setPages]               = useState([]);
  const [activePage, setActivePage]     = useState(null);
  const [docTree, setDocTree]           = useState(null);
  const [navPath, setNavPath]           = useState([]);
  const [rightPane, setRightPane]       = useState(null);
  const [modal, setModal]               = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingSchema, setEditingSchema] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [localData, setLocalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [editingSingleItem, setEditingSingleItem] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [showAI, setShowAI]             = useState(false);
  const [tab, setTab]                   = useState('editor');
  const [topSections, setTopSections] = useState([]);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  const [editingFieldLabel, setEditingFieldLabel] = useState(null);
  const [editingSectionLabel, setEditingSectionLabel] = useState(null);
  const [editingArrayDisplayField, setEditingArrayDisplayField] = useState(null);
  const [tables, setTables]             = useState([]);
  const [activeTable, setActiveTable]   = useState(null);
  const [renamingCrm, setRenamingCrm]   = useState(null);
  const [showCrmImport, setShowCrmImport] = useState(false);
  const [showCrmExport, setShowCrmExport] = useState(false);
  const [crmSelectedLists, setCrmSelectedLists] = useState([]);
  const [crmDragOver, setCrmDragOver] = useState(null);
  const [forms, setForms]               = useState([]);
  const [activeForm, setActiveForm]     = useState(null);
  const [duplicatingForm, setDuplicatingForm] = useState(null);
  const [activeView, setActiveView]     = useState('pages');
  const [newFormSection, setNewFormSection] = useState('forms_referrals'); // forms | forms_referrals
  // Categories are backed by each page's `category` field on the server (Schema model).
  // `extraCategories` holds categories created via "+ Category" that have no pages
  // assigned yet, so they still show up in the sidebar before first use.
  const [extraCategories, setExtraCategories] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [reviewConfirm, setReviewConfirm] = useState(null);
  const [reviewConfirmBusy, setReviewConfirmBusy] = useState(false);
  const reviewConfirmResolve = React.useRef(null);

  const showToast = (message, type = 'success') => {
    const id = 'toast-' + uid();
    setToasts(current => [...current, { id, message, type }].slice(-3));
    window.setTimeout(() => {
      setToasts(current => current.filter(toast => toast.id !== id));
    }, type === 'error' ? 4500 : 3000);
  };

  const pagesRef = React.useRef([]);
  pagesRef.current = pages;
  const [speakerNames, setSpeakerNames] = useState([]);
  const refreshSpeakerNames = React.useCallback(() => {
    fetchSpeakerNames(pagesRef.current).then(setSpeakerNames).catch(() => {});
  }, []);
  useEffect(() => { if (pages.length) refreshSpeakerNames(); }, [pages, refreshSpeakerNames]);
  const speakerCtx = React.useMemo(() => ({ names: speakerNames, refresh: refreshSpeakerNames }), [speakerNames, refreshSpeakerNames]);

  const autoSaveTimer = React.useRef(null);
  const localDataRef = React.useRef({});

  useEffect(() => {
    if (!project?._id) return;
    API.get('/api/schemas?projectId=' + project._id)
      .then(async r => {
        setPages(r.data || []);
        try {
          const titled = await API.post('/api/schemas/title-case', { projectId: project._id });
          if (Array.isArray(titled.data)) setPages(titled.data);
        } catch (e) {}
      })
      .catch(() => {});
    API.get('/api/si3tables?projectId=' + project._id)
      .then(async r => {
        try {
          const unified = await API.post('/api/si3tables/crm/unify', { projectId: project._id });
          try {
            const synced = await API.post('/api/si3tables/crm/sync-forms', { projectId: project._id });
            setTables(synced.data?.tables || unified.data?.tables || r.data || []);
          } catch (syncErr) {
            setTables(unified.data?.tables || r.data || []);
          }
        } catch (e) {
          setTables(r.data || []);
        }
      })
      .catch(() => {});
    API.get('/api/forms?projectId=' + project._id)
      .then(r => {
        const formsData = r.data?.data || r.data || [];
        console.log('✓ Forms loaded:', formsData.length, 'forms');
        setForms(Array.isArray(formsData) ? formsData : []);
      })
      .catch(err => {
        console.error('Forms API error:', err.message);
        setForms([]);
      });
  }, [project?._id]);

  // Derive the visible category list: real categories already used by pages,
  // plus any freshly-created-but-unassigned ones.
  const categories = React.useMemo(() => {
    const used = new Set(pages.map(p => p.category).filter(Boolean));
    extraCategories.forEach((name) => used.add(name));
    const names = [...PAGE_SECTION_ORDER];
    used.forEach((name) => {
      if (!names.includes(name)) names.push(name);
    });
    return names.map(name => ({ id: name, name }));
  }, [pages, extraCategories]);

  useEffect(() => {
    if (!activePage) return;
    API.get(`/api/documents?schemaId=${activePage._id}`).then(r => {
      const docs = r.data.map(doc => ({
        _id: doc._id,
        title: doc.title,
        label: doc.label,
        published: doc.published,
        tree: doc.data?.tree || { id: doc._id, name: doc.title, label: doc.title, children: [], fields: [], data: {} }
      }));
      setTopSections(docs);
      setDocTree(null);
      setNavPath([]);
      setRightPane(null);
    }).catch(() => {});
  }, [activePage]);

  const currentNode = docTree ? getNodeByPath(docTree.tree, navPath) : null;

  const breadcrumb = () => {
    if (!docTree) return [];
    const crumbs = [{ label: docTree.title, path: [] }];
    let node = docTree.tree;
    for (const id of navPath) {
      node = (node.children || []).find(c => c.id === id);
      if (!node) break;
      crumbs.push({ label: node.label || node.name, path: crumbs[crumbs.length - 1].path.concat(id) });
    }
    return crumbs;
  };

  const updateTree = (path, updater) => {
    setDocTree(prev => {
      const newTree = updateNodeByPath(prev.tree, path, updater);
      const updated = { ...prev, tree: newTree };
      setTopSections(ts => ts.map(s => s._id === prev._id ? updated : s));
      return updated;
    });
  };

  const handleAddPage = async (name) => {
    try {
      const titled = toTitleCaseLabel(name) || name;
      const res = await API.post('/api/schemas', { name: titled, slug: slugify(name), color: COLORS[pages.length % COLORS.length], fields: [], projectId: project?._id || null });
      setPages(p => [...p, res.data]);
      setActivePage(res.data);
    } catch(e) { alert('Error'); }
    setModal(null);
  };

  const handleAddCategory = (name) => {
    const trimmed = toTitleCaseLabel(name) || name.trim();
    if (!trimmed) return;
    if (!categories.some(c => c.name === trimmed)) {
      setExtraCategories(prev => [...prev, trimmed]);
    }
    setModal(null);
  };

  const handleAssignPageCategory = async (pageId, categoryName) => {
    // Optimistic UI update
    setPages(p => p.map(pg => pg._id === pageId ? { ...pg, category: categoryName || null } : pg));
    try {
      const res = await API.patch(`/api/schemas/${pageId}/category`, { category: categoryName || null });
      setPages(p => p.map(pg => pg._id === pageId ? res.data : pg));
    } catch (e) {
      alert('Error assigning category: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    if (!window.confirm('Delete this category? Its pages will move back to Pages.')) return;
    const affected = pages.filter(p => p.category === categoryName);
    setExtraCategories(prev => prev.filter(c => c !== categoryName));
    setPages(p => p.map(pg => pg.category === categoryName ? { ...pg, category: null } : pg));
    try {
      await Promise.all(affected.map(p => API.patch(`/api/schemas/${p._id}/category`, { category: null })));
    } catch (e) {
      alert('Error deleting category: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleAddTopSection = async (name, key, onError) => {
    if (!activePage) return;
    const existingKeys = topSections.map(s => s.tree?._key || '');
    if (existingKeys.includes(key)) {
      if (onError) onError('Key "' + key + '" already exists. Please choose a different one.');
      return;
    }
    const newTree = { id: uid(), _key: key, name, label: name, children: [], fields: [], data: {} };
    try {
      const res = await API.post('/api/documents', {
        schemaId: activePage._id, schemaSlug: activePage.slug,
        title: name, fields: [], data: { tree: newTree }, published: false
      });
      const doc = { _id: res.data._id, title: name, published: false, tree: newTree };
      setTopSections(p => [...p, doc]);
    } catch(e) { alert('Error'); }
    setModal(null);
  };

  const handleDeletePage = async (pageId) => {
    if (!window.confirm('Delete this page and ALL its sections?')) return;
    try {
      const docs = await API.get('/api/documents?schemaId=' + pageId);
      await Promise.all(docs.data.map(d => API.delete('/api/documents/' + d._id)));
      await API.delete('/api/schemas/' + pageId);
      setPages(p => p.filter(pg => pg._id !== pageId));
      if (activePage?._id === pageId) {
        setActivePage(null);
        setDocTree(null);
        setNavPath([]);
        setRightPane(null);
        setTopSections([]);
      }
    } catch(e) { alert('Error deleting page'); }
  };

  const dataTables = tables.filter(t => !isCrmTable(t));
  const crmTable = tables.find((t) => t?.section === 'crm')
    || tables.find((t) => crmNameKey(t?.name) === 'members')
    || tables.find(isCrmTable)
    || null;
  const crmLists = crmListNames(crmTable);

  const openCrm = (lists = []) => {
    setCrmSelectedLists(lists);
    setActiveView('crm');
    setActivePage(null);
    setActiveForm(null);
    setTab('editor');
    if (!crmTable?._id) return;
    setActiveTable(crmTable);
    API.get('/api/si3tables/' + crmTable._id).then(r => {
      setActiveTable(r.data);
      setTables(prev => prev.map(tbl => tbl._id === r.data._id ? r.data : tbl));
    }).catch(() => {});
  };

  const toggleCrmList = (name) => {
    setCrmSelectedLists((prev) => {
      const on = prev.some((item) => crmNameKey(item) === crmNameKey(name));
      if (on) return prev.filter((item) => crmNameKey(item) !== crmNameKey(name));
      return [...prev, name];
    });
    setActiveView('crm');
    setActivePage(null);
    setActiveForm(null);
    setTab('editor');
    if (crmTable) setActiveTable(crmTable);
  };

  const saveCrmLists = async (nextLists, extra = {}) => {
    if (!crmTable?._id) return null;
    const res = await API.put('/api/si3tables/' + crmTable._id, { crmLists: nextLists, ...extra });
    const updated = res.data;
    setTables(t => t.map(tbl => tbl._id === updated._id ? { ...tbl, ...updated } : tbl));
    setActiveTable(prev => prev && prev._id === updated._id ? { ...prev, ...updated } : prev);
    return updated;
  };

  const handleReorderCrmLists = async (fromName, toName) => {
    if (!fromName || !toName || crmNameKey(fromName) === crmNameKey(toName)) return;
    const next = [...crmLists];
    const from = next.findIndex((item) => crmNameKey(item) === crmNameKey(fromName));
    const to = next.findIndex((item) => crmNameKey(item) === crmNameKey(toName));
    if (from < 0 || to < 0) return;
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    try {
      await saveCrmLists(next);
    } catch (e) {
      alert('Error reordering lists: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleAddCrmCategory = async (name) => {
    const trimmed = toTitleCaseLabel(name) || String(name || '').trim();
    if (!trimmed) return;
    if (crmNameKey(trimmed) === 'common') {
      alert('Please use a program name (for example Grow3dge Members), not “common”.');
      return;
    }
    if (crmLists.some((item) => crmNameKey(item) === crmNameKey(trimmed))) {
      alert('That list already exists.');
      return;
    }
    try {
      let table = crmTable;
      if (!table?._id) {
        const unified = await API.post('/api/si3tables/crm/unify', { projectId: project?._id || null });
        setTables(unified.data?.tables || []);
        table = unified.data?.crmTable || (unified.data?.tables || []).find(isCrmTable);
      }
      if (!table?._id) throw new Error('CRM is not ready yet');
      const current = crmListNames(table);
      const res = await API.put('/api/si3tables/' + table._id, { crmLists: [...current, trimmed] });
      setTables(t => t.map(tbl => tbl._id === res.data._id ? { ...tbl, ...res.data } : tbl));
      setActiveTable(prev => prev && prev._id === res.data._id ? { ...prev, ...res.data } : res.data);
      setCrmSelectedLists([trimmed]);
      setActiveView('crm');
      setActivePage(null);
      setActiveForm(null);
      setTab('editor');
    } catch(e) { alert('Error creating list: ' + (e.response?.data?.error || e.message)); }
    setModal(null);
  };

  const handleRenameCrmCategory = async (name) => {
    const nextName = toTitleCaseLabel(name) || String(name || '').trim();
    const oldName = renamingCrm;
    if (!nextName || !oldName || !crmTable?._id) return;
    try {
      const nextLists = crmLists.map((item) => crmNameKey(item) === crmNameKey(oldName) ? nextName : item);
      const col = crmListsColumn(crmTable);
      const rows = crmTable.rows || [];
      if (col) {
        await Promise.all(rows.map((row) => {
          const tags = rowListTags(crmTable, row);
          if (!tags.some((tag) => crmNameKey(tag) === crmNameKey(oldName))) return null;
          const formatted = formatCrmListTags(tags.map((tag) => crmNameKey(tag) === crmNameKey(oldName) ? nextName : tag));
          const data = {
            ...(row.data || {}),
            [col.id]: formatted,
            Lists: formatted
          };
          if (col.name) data[col.name] = formatted;
          return API.put(`/api/si3tables/${crmTable._id}/rows/${row.rowId}`, { data });
        }).filter(Boolean));
      }
      await saveCrmLists(nextLists);
      await Promise.all((crmTable.columns || []).map((column) => {
        const vis = column.visibleOnLists;
        if (!Array.isArray(vis) || !vis.some((item) => crmNameKey(item) === crmNameKey(oldName))) return null;
        return API.put(`/api/si3tables/${crmTable._id}/columns/${column.id}`, {
          visibleOnLists: vis.map((item) => crmNameKey(item) === crmNameKey(oldName) ? nextName : item)
        });
      }).filter(Boolean));
      const full = await API.get('/api/si3tables/' + crmTable._id);
      setActiveTable(full.data);
      setTables(t => t.map(tbl => tbl._id === full.data._id ? full.data : tbl));
      setCrmSelectedLists((prev) => prev.map((item) => crmNameKey(item) === crmNameKey(oldName) ? nextName : item));
    } catch(e) { alert('Error renaming list: ' + (e.response?.data?.error || e.message)); }
    setRenamingCrm(null);
    setModal(null);
  };

  const handleDeleteCrmCategory = async (listName) => {
    if (!window.confirm('Remove this list? Members stay in All Members; they are just untagged from this list.')) return;
    if (!crmTable?._id) return;
    try {
      const nextLists = crmLists.filter((item) => crmNameKey(item) !== crmNameKey(listName));
      const col = crmListsColumn(crmTable);
      const rows = crmTable.rows || [];
      if (col) {
        await Promise.all(rows.map((row) => {
          const tags = rowListTags(crmTable, row);
          if (!tags.some((tag) => crmNameKey(tag) === crmNameKey(listName))) return null;
          const formatted = formatCrmListTags(tags.filter((tag) => crmNameKey(tag) !== crmNameKey(listName)));
          const data = {
            ...(row.data || {}),
            [col.id]: formatted,
            Lists: formatted
          };
          if (col.name) data[col.name] = formatted;
          return API.put(`/api/si3tables/${crmTable._id}/rows/${row.rowId}`, { data });
        }).filter(Boolean));
      }
      await saveCrmLists(nextLists);
      const full = await API.get('/api/si3tables/' + crmTable._id);
      setActiveTable(full.data);
      setTables(t => t.map(tbl => tbl._id === full.data._id ? full.data : tbl));
      setCrmSelectedLists((prev) => prev.filter((item) => crmNameKey(item) !== crmNameKey(listName)));
    } catch(e) { alert('Error deleting list'); }
  };

  const handleCrmImport = async (listName, rows) => {
    try {
      if (!crmTable?._id) throw new Error('CRM is not ready yet');
      const res = await API.post(`/api/si3tables/${crmTable._id}/import`, { rows, listTag: listName || '' });
      const table = res.data?.table;
      if (!table) throw new Error('Import did not return members');
      setTables(t => t.map(tbl => tbl._id === table._id ? table : tbl));
      setActiveTable(table);
      if (listName) setCrmSelectedLists([listName]);
      setActiveView('crm');
      setTab('editor');
      setShowCrmImport(false);
    } catch (e) {
      throw new Error(e.response?.data?.error || e.message || 'Import failed');
    }
  };

  const handleAddTable = async (name) => {
    try {
      const res = await API.post('/api/si3tables', {
        name,
        projectId: project?._id || null,
        section: 'tables',
        description: '',
        columns: [
          { id: 'col-1', name: 'Name', type: 'string', required: true, defaultValue: null }
        ]
      });
      setTables(t => [...t, res.data]);
      setActiveTable(res.data);
      setActiveView('tables');
    } catch(e) { alert('Error creating table: ' + (e.response?.data?.error || e.message)); }
    setModal(null);
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('Delete this table and ALL its data?')) return;
    try {
      await API.delete('/api/si3tables/' + tableId);
      setTables(t => t.filter(tbl => tbl._id !== tableId));
      if (activeTable?._id === tableId) {
        setActiveTable(null);
      }
    } catch(e) { alert('Error deleting table'); }
  };

  const handleAddTableRow = async (data) => {
    if (!activeTable?._id) {
      alert('Error: Table not loaded properly');
      return;
    }
    try {
      const rowId = 'row-' + uid();
      let payload = { ...(data || {}) };
      if (activeView === 'crm') {
        const col = crmListsColumn(activeTable);
        if (col && crmSelectedLists.length && !payload[col.id] && !payload.Lists) {
          payload[col.id] = formatCrmListTags(crmSelectedLists);
        }
      }
      const res = await API.post(`/api/si3tables/${activeTable._id}/rows`, { rowId, data: payload });
      const table = res.data?.table || res.data;
      setActiveTable(table);
      setTables(t => t.map(tbl => tbl._id === table._id ? table : tbl));
      showToast(isCrmTable(activeTable) ? 'Member added' : 'Row added');
    } catch(e) { showToast('Error adding row: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const handleUpdateTableRow = async (rowId, data) => {
    if (!activeTable?._id) {
      alert('Error: Table not loaded properly');
      return;
    }
    try {
      const res = await API.put(`/api/si3tables/${activeTable._id}/rows/${rowId}`, { data });
      const table = res.data?.table || res.data;
      setActiveTable(table);
      setTables(t => t.map(tbl => tbl._id === table._id ? table : tbl));
      showToast('Changes saved');
    } catch(e) { showToast('Error updating row: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const reviewInFlight = React.useRef(false);
  const askReviewConfirm = (options) => new Promise((resolve) => {
    reviewConfirmResolve.current = resolve;
    setReviewConfirm(options);
  });
  const confirmReviewAction = () => {
    setReviewConfirmBusy(true);
    const resolve = reviewConfirmResolve.current;
    reviewConfirmResolve.current = null;
    if (resolve) resolve(true);
  };
  const cancelReviewConfirm = () => {
    if (reviewConfirmBusy) return;
    const resolve = reviewConfirmResolve.current;
    reviewConfirmResolve.current = null;
    if (resolve) resolve(false);
    setReviewConfirm(null);
  };
  const closeReviewConfirm = () => {
    reviewConfirmResolve.current = null;
    setReviewConfirm(null);
    setReviewConfirmBusy(false);
  };
  const handleReviewApplicant = async (rowId, action, app) => {
    const table = (activeTable && isCrmTable(activeTable) ? activeTable : crmTable);
    if (!table?._id) {
      alert('CRM is not loaded');
      return;
    }
    // Guard against a second click landing while the first request is still
    // out, which would review (and email) the same person twice.
    if (reviewInFlight.current) return;
    reviewInFlight.current = true;
    try {
      const which = app?.label ? `their ${app.label} application` : 'this applicant';
      const ok = await askReviewConfirm({
        title: action === 'approve'
          ? 'Approve applicant'
          : action === 'ineligible'
          ? 'Mark applicant as not eligible'
          : 'Deny applicant',
        message: action === 'approve'
          ? `Approve ${which}? They will be added to the Loops Approved list and sent the approved email.`
          : action === 'ineligible'
          ? `Mark ${which} as not eligible? They will be sent the not-eligible email.`
          : `Deny ${which}? They will be sent the not-moving-forward email.`,
        confirmLabel: action === 'approve'
          ? 'Approve'
          : action === 'ineligible'
          ? 'Mark as not eligible'
          : 'Deny',
        busyLabel: action === 'approve'
          ? 'Approving...'
          : action === 'ineligible'
          ? 'Marking...'
          : 'Denying...'
      });
      if (!ok) return;
      const res = await API.post(`/api/si3tables/${table._id}/rows/${rowId}/review`, {
        action,
        application: app?.key
      });
      const next = res.data?.table || res.data;
      setActiveTable(next);
      setTables(t => t.map(tbl => tbl._id === next._id ? next : tbl));
      const failed = (res.data?.results || []).filter(r => r?.email && r.email.ok === false);
      if (failed.length) {
        showToast(
          'CRM list updated, but the email did not send (' +
          failed.map(r => `${r.application}: ${r.email.reason}`).join('; ') +
          '). Check in Loops that the approved/denied template exists and is published.',
          'error'
        );
      } else if (res.data?.email && res.data.email.ok === false) {
        showToast('CRM list updated, but the email did not send. Check Loops that the approved/denied template is published.', 'error');
      } else {
        showToast(
          action === 'approve'
            ? 'Applicant approved'
            : action === 'ineligible'
            ? 'Applicant marked as not eligible'
            : 'Applicant denied'
        );
      }
    } catch (e) {
      showToast(e.response?.data?.error || e.message, 'error');
    } finally {
      closeReviewConfirm();
      reviewInFlight.current = false;
    }
  };

  const paidInFlight = React.useRef(false);
  const handleMarkPaid = async (rowId) => {
    const table = (activeTable && isCrmTable(activeTable) ? activeTable : crmTable);
    if (!table?._id) {
      alert('CRM is not loaded');
      return;
    }
    if (paidInFlight.current) return;
    paidInFlight.current = true;
    try {
      const ok = await askReviewConfirm({
        title: 'Mark applicant as paid',
        message: 'Mark this applicant as paid? They will be sent the onboarding email.',
        confirmLabel: 'Mark paid',
        busyLabel: 'Marking paid...'
      });
      if (!ok) return;
      const res = await API.post(`/api/si3tables/${table._id}/rows/${rowId}/paid`);
      const next = res.data?.table || res.data;
      setActiveTable(next);
      setTables(t => t.map(tbl => tbl._id === next._id ? next : tbl));
      showToast('Applicant marked as paid');
    } catch (e) {
      showToast(e.response?.data?.error || e.message, 'error');
    } finally {
      closeReviewConfirm();
      paidInFlight.current = false;
    }
  };

  const handleDeleteTableRow = async (rowId) => {
    const table = (activeView === 'crm'
      ? ((activeTable && isCrmTable(activeTable)) ? activeTable : crmTable)
      : activeTable);
    if (!table?._id) {
      alert('Error: Table not loaded properly');
      return;
    }
    try {
      await API.delete(`/api/si3tables/${table._id}/rows/${rowId}`);
      const dropRow = (tbl) => (tbl && tbl._id === table._id)
        ? { ...tbl, rows: (tbl.rows || []).filter((r) => String(r.rowId) !== String(rowId) && String(r._id) !== String(rowId)) }
        : tbl;
      setActiveTable((t) => dropRow(t));
      setTables((ts) => ts.map(dropRow));
      showToast('Row deleted');
    } catch(e) { showToast('Error deleting row: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const handleAddTableColumn = async (column) => {
    if (!activeTable?._id) {
      alert('Error: Table not loaded properly');
      return;
    }
    try {
      const payload = { ...column };
      if (activeView === 'crm' && crmSelectedLists.length && !Array.isArray(payload.visibleOnLists)) {
        payload.visibleOnLists = crmSelectedLists;
      }
      const res = await API.post(`/api/si3tables/${activeTable._id}/columns`, payload);
      const table = res.data?.table || res.data;
      setActiveTable(table);
      setTables(t => t.map(tbl => tbl._id === table._id ? table : tbl));
      showToast('Field added');
    } catch(e) { showToast('Error adding column: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const handleUpdateTableColumn = async (columnId, updates) => {
    if (!activeTable?._id) {
      alert('Error: Table not loaded properly');
      return;
    }
    try {
      const res = await API.put(`/api/si3tables/${activeTable._id}/columns/${columnId}`, updates);
      const table = res.data?.table || res.data;
      setActiveTable(table);
      setTables(t => t.map(tbl => tbl._id === table._id ? table : tbl));
    } catch(e) { alert('Error updating field: ' + (e.response?.data?.error || e.message)); }
  };

  const handleDeleteTableColumn = async (columnId) => {
    if (!activeTable?._id) {
      alert('Error: Table not loaded properly');
      return;
    }
    try {
      const res = await API.delete(`/api/si3tables/${activeTable._id}/columns/${columnId}`);
      const table = res.data?.table || res.data;
      setActiveTable(table);
      setTables(t => t.map(tbl => tbl._id === table._id ? table : tbl));
      showToast('Field deleted');
    } catch(e) { showToast('Error deleting column: ' + (e.response?.data?.error || e.message), 'error'); }
  };

  const isFormsReferralsForm = (f) => {
    if (!f) return false;
    if (f.formsReferralsSection === true) return true;
    // Treat Si Her DeFi / known new forms as Forms + Referrals even before flag is saved
    const slug = String(f.slug || '').toLowerCase();
    const title = String(f.title || '').toLowerCase();
    return slug.includes('siherdefi') || slug.includes('si-her-defi') || title.includes('si her defi');
  };

  const handleAddForm = async (name, templateId = 'blank') => {
    if (!project?._id) return;
    try {
      const template = FORM_TEMPLATES[templateId] || FORM_TEMPLATES.blank;
      const fields = buildTemplateFields(templateId);
      const inReferrals = newFormSection === 'forms_referrals' || template.formsReferralsSection === true || templateId === 'si-her-defi';
      const res = await API.post('/api/forms', {
        projectId: project._id,
        title: name.trim(),
        slug: template.slug || slugify(name.trim()),
        description: template.description || '',
        image: '',
        meta: { title: name.trim(), description: template.description || '', image: '' },
        tableName: '',
        loopsListId: '',
        showLoadingScreen: false,
        formsReferralsSection: inReferrals,
        buttonColor: template.buttonColor || '#6B46FE',
        fields
      });
      const created = res.data?.data || res.data;
      setForms(f => Array.isArray(f) ? [...f, created] : [created]);
      setActiveForm(created);
      setActiveView('forms');

      try {
        const tablesRes = await API.get(`/api/si3tables?projectId=${project._id}`);
        setTables(tablesRes.data || []);
      } catch(e) { console.error('Error refreshing tables:', e); }
    } catch(e) { alert('Error creating form: ' + (e.response?.data?.error || e.message)); }
    setModal(null);
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Delete this form?')) return;
    try {
      await API.delete(`/api/forms/${formId}`);
      setForms(f => Array.isArray(f) ? f.filter(fm => fm._id !== formId) : []);
      if (activeForm?._id === formId) setActiveForm(null);

      // Refresh si3tables list to reflect any changes
      if (project?._id) {
        try {
          const tablesRes = await API.get(`/api/si3tables?projectId=${project._id}`);
          setTables(tablesRes.data || []);
        } catch(e) { console.error('Error refreshing tables:', e); }
      }
    } catch(e) { alert('Error deleting form'); }
  };

  const handleDuplicateForm = async (formId, name, slug) => {
    try {
      const res = await API.post(`/api/forms/${formId}/duplicate`, { name, slug });
      const duplicatedForm = res.data?.data || res.data;
      setForms(f => Array.isArray(f) ? [...f, duplicatedForm] : [duplicatedForm]);
      setDuplicatingForm(null);
      setActiveForm(duplicatedForm);
      alert('Form duplicated. You can now set a different brand button color for this copy.');

      // Refresh si3tables list (new table was created for the duplicated form)
      if (project?._id) {
        try {
          const tablesRes = await API.get(`/api/si3tables?projectId=${project._id}`);
          setTables(tablesRes.data || []);
        } catch(e) { console.error('Error refreshing tables:', e); }
      }
    } catch(e) {
      console.error('Error duplicating form:', e);
      alert('Error duplicating form: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleUpdateForm = async (formData) => {
    const formId = formData?._id || activeForm?._id;
    if (!formId) {
      console.error('❌ No form ID for update. formData:', formData, 'activeForm:', activeForm);
      throw new Error('No form selected to save');
    }

    const url = `/api/forms/${formId}`;
    const res = await API.put(url, formData);
    const updatedForm = res.data?.data || res.data;
    setForms(f => Array.isArray(f) ? f.map(fm => fm._id === formId ? updatedForm : fm) : [updatedForm]);
    setActiveForm(current => (current?._id === formId ? updatedForm : current));
    return updatedForm;
  };

  const handleDeleteTopSection = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    await API.delete(`/api/documents/${id}`);
    setTopSections(p => p.filter(s => s._id !== id));
    if (docTree?._id === id) { setDocTree(null); setNavPath([]); setRightPane(null); }
  };

  const handleTopSectionClick = (s) => {
    API.get('/api/documents/' + s._id).then(r => {
      const doc = r.data;
      const tree = doc.data?.tree || { id: doc._id, name: doc.title, label: doc.title, children: [], fields: [], data: {} };
      const freshDoc = { _id: doc._id, title: doc.title, published: doc.published, tree };
      setDocTree(freshDoc);
      setNavPath([]);
      if ((tree.fields || []).length > 0) {
        setRightPane({ ...tree, path: [] });
      } else {
        setRightPane(null);
      }
    }).catch(() => {
      setDocTree(s);
      setNavPath([]);
      setRightPane(null);
    });
  };

  const handleNavigateInto = (child) => {
    setNavPath(p => [...p, child.id]);
    setRightPane(null);
  };

  const handleSectionClick = (child) => {
    const freshChild = docTree ? getNodeByPath(docTree.tree, [...navPath, child.id]) || child : child;
    const hasChildren = (freshChild.children || []).length > 0;
    const hasFields = (freshChild.fields || []).length > 0;
    if (hasChildren) {
      setNavPath(p => [...p, child.id]);
      setRightPane(null);
    } else if (hasFields) {
      setRightPane({ ...freshChild, path: [...navPath, child.id] });
    } else {
      setNavPath(p => [...p, child.id]);
      setRightPane(null);
    }
  };

  const handleAddSubSection = (name, key) => {
    const newChild = { id: uid(), _key: key, name, label: name, children: [], fields: [], data: {} };
    updateTree(navPath, node => ({ ...node, children: [...(node.children || []), newChild] }));
    setModal(null);
  };

  const handleAddField = async (field) => {
    if (!rightPane || !docTree) return;
    const defaultVal = field.type==='array'?[]:field.type==='boolean'?false:field.type==='number'?0:'';

    console.log('handleAddField - Original field:', field);

    // Clean up empty previewTemplate values (only remove if explicitly empty string)
    const cleanField = { ...field };
    if (cleanField.previewTemplate === '') {
      delete cleanField.previewTemplate;
    }
    // Clean up empty previewTemplate in array sub-fields
    if (cleanField.type === 'array' && cleanField.subFields) {
      console.log('Array field detected. Original subFields:', cleanField.subFields);
      cleanField.subFields = cleanField.subFields.map(sf => {
        const cleanSf = { ...sf };
        if (cleanSf.previewTemplate === '') {
          delete cleanSf.previewTemplate;
        }
        return cleanSf;
      });
      console.log('Clean subFields being sent:', cleanField.subFields);
    }

    const updatedFields = [...(rightPane.fields || []), cleanField];
    const updatedData = { ...(rightPane.data || {}), [field.name]: defaultVal };

    const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
      ...node,
      fields: updatedFields,
      data: updatedData
    }));

    updateTree(rightPane.path, node => ({
      ...node,
      fields: updatedFields,
      data: updatedData
    }));

    setRightPane(prev => ({
      ...prev,
      fields: updatedFields,
      data: updatedData
    }));

    try {
      await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch(e) {
      console.error('Error saving field:', e.message);
      setAutoSaveStatus(null);
    }

    setModal(null);
  };

  const handleUpdateSubFields = async (fieldId, newSubFields, contextDocTree, contextRightPane) => {
    // Use passed context or fall back to parent state
    const useDocTree = contextDocTree || docTree;
    const useRightPane = contextRightPane || rightPane;

    console.log('handleUpdateSubFields called with fieldId:', fieldId);
    console.log('useDocTree:', useDocTree);
    console.log('useRightPane:', useRightPane);

    if (!useRightPane || !useDocTree) {
      console.warn('⚠️ EXITING EARLY: useRightPane or useDocTree is not set!');
      return;
    }

    console.log('=== handleUpdateSubFields START ===');
    console.log('Received newSubFields:', newSubFields);
    console.log('Each sub-field details:');
    newSubFields.forEach((sf, idx) => {
      console.log(`  [${idx}] name: ${sf.name}, type: ${sf.type}, previewTemplate: "${sf.previewTemplate}"`);
    });

    // Clean up empty previewTemplate in sub-fields (only remove if explicitly empty string)
    const cleanSubFields = newSubFields.map(sf => {
      const cleanSf = { ...sf };
      if (cleanSf.previewTemplate === '') {
        delete cleanSf.previewTemplate;
      }
      return cleanSf;
    });

    console.log('After cleanup, cleanSubFields being sent:', cleanSubFields);
    console.log('Each clean sub-field details:');
    cleanSubFields.forEach((sf, idx) => {
      console.log(`  [${idx}] name: ${sf.name}, type: ${sf.type}, previewTemplate: "${sf.previewTemplate}"`);
    });

    const updatedFields = (useRightPane.fields || []).map(f =>
      f.id === fieldId ? { ...f, subFields: cleanSubFields } : f
    );

    const updatedTree = updateNodeByPath(useDocTree.tree, useRightPane.path, node => ({
      ...node,
      fields: updatedFields
    }));

    updateTree(useRightPane.path, node => ({ ...node, fields: updatedFields }));
    setRightPane(prev => ({ ...prev, fields: updatedFields }));
    try {
      const apiPayload = {
        title: useDocTree.title,
        data: { tree: updatedTree }
      };
      console.log('=== SENDING API REQUEST ===');
      console.log('API Endpoint:', '/api/documents/' + useDocTree._id);
      console.log('Full payload:', JSON.stringify(apiPayload, null, 2));

      const response = await API.put('/api/documents/' + useDocTree._id, apiPayload);
      console.log('API Response:', response.data);
      console.log('✓ Successfully saved!');
    } catch(e) {
      console.error('Save error', e);
      alert('Error saving: ' + e.message);
    }
    setEditingSchema(null);
  };

  const handleReorder = async (newOrder) => {
    setTopSections(newOrder);
    try {
      await API.post('/api/documents/reorder', { ids: newOrder.map(s => s._id) });
    } catch(e) { console.error('Reorder failed', e); }
  };

  useEffect(() => {
    if (rightPane) {
      setLocalData(rightPane.data || {});
      setHasChanges(false);
      setAutoSaveStatus(null);
      clearTimeout(autoSaveTimer.current);
    }
  }, [rightPane?.id]);

  useEffect(() => {
    localDataRef.current = localData;
  }, [localData]);

  const handleLocalChange = (fieldName, value) => {
    setLocalData(prev => ({ ...prev, [fieldName]: value }));
    setHasChanges(true);
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      triggerAutoSave();
    }, 1500);
  };

  const triggerAutoSave = async () => {
    if (!rightPane || !docTree) return;
    const currentLocalData = localDataRef.current;
    setAutoSaveStatus('saving');
    try {
      updateTree(rightPane.path, node => ({ ...node, data: { ...(node.data || {}), ...currentLocalData } }));
      setRightPane(prev => ({ ...prev, data: { ...(prev.data || {}), ...currentLocalData } }));
      const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
        ...node,
        fields: rightPane.fields,
        data: { ...(node.data || {}), ...currentLocalData }
      }));
      await API.put('/api/documents/' + docTree._id, {
        title: docTree.title,
        data: { tree: updatedTree },
        published: docTree.published
      });
      setHasChanges(false);
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch(e) {
      console.error('Auto-save failed:', e);
      setAutoSaveStatus(null);
    }
  };

  const handleSaveInline = async () => {
    clearTimeout(autoSaveTimer.current);
    await triggerAutoSave();
  };

  const handleDiscardInline = () => {
    setLocalData(rightPane?.data || {});
    setHasChanges(false);
  };

  const handleFieldSave = (fieldName, value) => {
    if (!rightPane) return;
    updateTree(rightPane.path, node => ({ ...node, data: { ...(node.data || {}), [fieldName]: value } }));
    setRightPane(prev => ({ ...prev, data: { ...(prev.data || {}), [fieldName]: value } }));
  };

  const handleDeleteField = async (fieldId) => {
    if (!window.confirm('Remove this field?')) return;
    if (!docTree) return;

    const updatedFields = (rightPane.fields || []).filter(f => f.id !== fieldId);
    const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
      ...node,
      fields: updatedFields
    }));

    updateTree(rightPane.path, node => ({ ...node, fields: updatedFields }));
    setRightPane(prev => ({ ...prev, fields: updatedFields }));

    try {
      await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch(e) {
      console.error('Error deleting field:', e.message);
      setAutoSaveStatus(null);
    }
  };

  const handleDeleteChild = (childId) => {
    if (!window.confirm('Delete this section?')) return;
    updateTree(navPath, node => ({ ...node, children: (node.children || []).filter(c => c.id !== childId) }));
  };

  const handleSave = async (publish = false) => {
    if (!docTree) return;
    setSaving(true);
    try {
      const res = await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: docTree.tree },
        published: publish || docTree.published
      });
      setDocTree(prev => ({ ...prev, published: res.data.published }));
      setTopSections(ts => ts.map(s => s._id === docTree._id ? { ...s, published: res.data.published, tree: docTree.tree } : s));
      alert('Saved successfully!');
    } catch(e) { alert('Error saving: ' + e.message); }
    setSaving(false);
  };

  const handleUnpublish = async () => {
    if (!docTree) return;
    setSaving(true);
    try {
      const res = await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: docTree.tree },
        published: false
      });
      setDocTree(prev => ({ ...prev, published: false }));
      setTopSections(ts => ts.map(s => s._id === docTree._id ? { ...s, published: false } : s));
    } catch(e) { alert('Error: ' + e.message); }
    setSaving(false);
  };

  const handleUpdateFieldLabel = async (newLabel) => {
    if (!rightPane || !docTree || !editingFieldLabel) return;
    if (!newLabel.trim()) {
      alert('Label cannot be empty');
      return;
    }
    try {
      const updatedFields = (rightPane.fields || []).map(f =>
        f.id === editingFieldLabel.id ? { ...f, label: newLabel.trim() } : f
      );

      const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
        ...node,
        fields: updatedFields
      }));

      updateTree(rightPane.path, node => ({ ...node, fields: updatedFields }));
      setRightPane(prev => ({ ...prev, fields: updatedFields }));

      await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
      setEditingFieldLabel(null);
    } catch(e) { alert('Error updating field label: ' + e.message); }
  };

  const handleUpdateSectionLabel = async (newLabel) => {
    if (!editingSectionLabel) return;
    if (!newLabel.trim()) {
      alert('Label cannot be empty');
      return;
    }
    try {
      if (editingSectionLabel.isTopSection) {
        // Update parent section label only (keep title unchanged)
        await API.put(`/api/documents/${editingSectionLabel._id}`, {
          title: editingSectionLabel.title,
          data: editingSectionLabel.data || { tree: editingSectionLabel.tree },
          published: editingSectionLabel.published,
          label: newLabel.trim()
        });
        setTopSections(ts => ts.map(s => s._id === editingSectionLabel._id ? { ...s, label: newLabel.trim() } : s));
      } else {
        // Update child section label
        if (!docTree) return;
        const updatedTree = updateNodeByPath(docTree.tree, editingSectionLabel.path, node => ({
          ...node,
          label: newLabel.trim()
        }));

        updateTree(editingSectionLabel.path, node => ({ ...node, label: newLabel.trim() }));

        await API.put(`/api/documents/${docTree._id}`, {
          title: docTree.title,
          data: { tree: updatedTree }
        });
      }
      setEditingSectionLabel(null);
    } catch(e) { alert('Error updating section label: ' + e.message); }
  };

  const handleUpdateArrayDisplayField = async (newDisplayField) => {
    if (!rightPane || !docTree || !editingArrayDisplayField) return;
    if (!newDisplayField.trim()) {
      alert('Display field cannot be empty');
      return;
    }
    try {
      const updatedFields = (rightPane.fields || []).map(f =>
        f.id === editingArrayDisplayField.id ? { ...f, displayField: newDisplayField.trim() } : f
      );

      const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
        ...node,
        fields: updatedFields
      }));

      updateTree(rightPane.path, node => ({ ...node, fields: updatedFields }));
      setRightPane(prev => ({ ...prev, fields: updatedFields }));

      await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
      setEditingArrayDisplayField(null);
    } catch(e) { alert('Error updating array display field: ' + e.message); }
  };

  const handleUpdateField = async (fieldId, changes) => {
    if (!rightPane || !docTree) return;
    try {
      // Clean up empty previewTemplate (only remove if explicitly empty string)
      const cleanChanges = { ...changes };
      if (cleanChanges.previewTemplate === '') {
        delete cleanChanges.previewTemplate;
      }

      const updatedFields = (rightPane.fields || []).map(f =>
        f.id === fieldId ? { ...f, ...cleanChanges } : f
      );

      const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({
        ...node,
        fields: updatedFields
      }));

      updateTree(rightPane.path, node => ({ ...node, fields: updatedFields }));
      setRightPane(prev => ({ ...prev, fields: updatedFields }));

      await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
    } catch(e) { console.error('Error updating field:', e.message); }
  };

  const draggedFieldId = React.useRef(null);
  const [fieldDropTarget, setFieldDropTarget] = useState(null);
  const handleReorderFields = async (fromId, toId) => {
    if (!rightPane || !docTree || !fromId || fromId === toId) return;
    const fields = [...(rightPane.fields || [])];
    const from = fields.findIndex(f => f.id === fromId);
    const to = fields.findIndex(f => f.id === toId);
    if (from < 0 || to < 0) return;
    const [moved] = fields.splice(from, 1);
    fields.splice(to, 0, moved);
    const updatedTree = updateNodeByPath(docTree.tree, rightPane.path, node => ({ ...node, fields }));
    updateTree(rightPane.path, node => ({ ...node, fields }));
    setRightPane(prev => ({ ...prev, fields }));
    try {
      await API.put(`/api/documents/${docTree._id}`, {
        title: docTree.title,
        data: { tree: updatedTree }
      });
      setAutoSaveStatus('saved');
      setTimeout(() => setAutoSaveStatus(null), 2000);
    } catch(e) { console.error('Error reordering fields:', e.message); }
  };

  const crumbs = breadcrumb();

  // Renders a single page item row in the sidebar, including the category assignment dropdown.
  const renderPageItem = (p) => (
    <div key={p._id} className={`page-item ${activePage?._id === p._id && activeView === 'pages' ? 'active' : ''}`}
      onClick={() => { setActiveView('pages'); setActivePage(p); setDocTree(null); setNavPath([]); setRightPane(null); }}>
      <div className="page-dot" style={{background: p.color}} />
      <span style={{flex:1}}>{p.name}</span>
      {categories.length > 0 && (
        <select
          className="page-cat-select"
          value={p.category || ''}
          onClick={e => e.stopPropagation()}
          onChange={e => handleAssignPageCategory(p._id, e.target.value)}
          style={{fontSize:10,maxWidth:66,border:'none',background:'transparent',color:'var(--muted)',cursor:'pointer'}}
          title="Assign to category">
          <option value="">Pages</option>
          {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
      )}
      <button className="page-del-btn" onClick={e => { e.stopPropagation(); handleDeletePage(p._id); }} title="Delete page">×</button>
    </div>
  );

  const uncategorizedPages = pages.filter(p => !p.category);

  return (
    <SpeakerNamesContext.Provider value={speakerCtx}>
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">SI3</div>
          <div className="brand-info">
            <span className="brand-name">SI3 CMS</span>
            {project && <span className="brand-project">{project.name}</span>}
          </div>
        </div>
        <div className="sidebar-section">
        <div className="sidebar-label">Pages</div>
        <nav className="page-nav">
          {uncategorizedPages.map(renderPageItem)}
          {uncategorizedPages.length === 0 && categories.length === 0 && <div className="nav-empty">No pages yet</div>}

          {categories.map(cat => {
            const catPages = sortPagesByStructure(pages.filter(p => p.category === cat.name));
            return (
              <div key={cat.id} style={{marginTop:10}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 8px',fontSize:11,fontWeight:600,color:'var(--muted)',textTransform:'uppercase',letterSpacing:'.03em'}}>
                  <span>{cat.name}</span>
                  <button className="page-del-btn" onClick={() => handleDeleteCategory(cat.name)} title="Delete category">×</button>
                </div>
                {catPages.map(renderPageItem)}
                {catPages.length === 0 && <div className="nav-empty" style={{paddingLeft:16,fontSize:11}}>No pages here</div>}
              </div>
            );
          })}
        </nav>
        <div style={{display:'flex',gap:6}}>
          <button className="sidebar-btn" style={{flex:1}} onClick={() => setModal('addPage')}>+ New Page</button>
          <button className="sidebar-btn" style={{flex:1}} onClick={() => setModal('addCategory')}>+ Category</button>
        </div>
        {activePage && activeView === 'pages' && <button className="sidebar-btn ai-sidebar-btn" onClick={() => setShowAI(true)}>✦ AI Builder</button>}
        </div>

        <div className="sidebar-section">
        <div className="sidebar-label">CRM</div>
        <nav className="page-nav">
          <div
            className={`page-item ${activeView === 'crm' && crmSelectedLists.length === 0 ? 'active' : ''}`}
            onClick={() => openCrm([])}
          >
            <div className="page-dot" style={{background: '#22c55e'}} />
            <span className="crm-list-name">All Members</span>
            <span className="nav-item-count">{crmTable?.rowCount ?? (crmTable?.rows || []).length ?? 0}</span>
          </div>
          {crmLists.map((name) => (
            <div
              key={name}
              className={`page-item ${activeView === 'crm' && crmSelectedLists.some((item) => crmNameKey(item) === crmNameKey(name)) ? 'active' : ''} ${crmDragOver === name ? 'drag-over' : ''}`}
              draggable={userRole !== 'viewer'}
              onDragStart={e => { e.dataTransfer.setData('crm-list', name); }}
              onDragOver={e => { e.preventDefault(); setCrmDragOver(name); }}
              onDragLeave={() => setCrmDragOver(null)}
              onDrop={e => {
                e.preventDefault();
                setCrmDragOver(null);
                const fromName = e.dataTransfer.getData('crm-list');
                handleReorderCrmLists(fromName, name);
              }}
              onClick={() => toggleCrmList(name)}
            >
              {userRole !== 'viewer' && <span className="drag-handle" title="Drag to reorder">⠿</span>}
              <div className="page-dot" style={{background: '#22c55e'}} />
              <span className="crm-list-name">{name}</span>
              <span className="nav-item-count">{countCrmList(crmTable, name)}</span>
              {userRole !== 'viewer' && (
                <button className="page-del-btn" onClick={e => { e.stopPropagation(); setRenamingCrm(name); setModal('renameCrm'); }} title="Rename list" style={{fontSize:13}}>✎</button>
              )}
              <button className="page-del-btn" onClick={e => { e.stopPropagation(); handleDeleteCrmCategory(name); }} title="Remove list">×</button>
            </div>
          ))}
          {!crmTable && <div className="nav-empty">No members yet</div>}
        </nav>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          <button className="sidebar-btn" style={{flex:1}} onClick={() => setModal('addCrmCategory')}>+ List</button>
          <button className="sidebar-btn" style={{flex:1}} onClick={() => { openCrm(crmSelectedLists); setShowCrmImport(true); }}>Import CSV</button>
{userRole !== 'viewer' && (<button className="sidebar-btn" style={{flex:1}} onClick={() => { openCrm(crmSelectedLists); setShowCrmExport(true); }}>Export CSV</button>)}
        </div>

        </div>

        <div className="sidebar-section">
        <div className="sidebar-label">Forms</div>
        <nav className="page-nav">
          {Array.isArray(forms) && forms.filter(f => !isFormsReferralsForm(f)).map(f => (
            <div key={f._id} className={`page-item ${activeForm?._id === f._id && activeView === 'forms' ? 'active' : ''}`}
              onClick={() => {
                console.log('Selected form:', f._id, f.title);
                setActiveView('forms');
                setActiveForm(f);
                setActivePage(null);
                setActiveTable(null);
              }}>
              <div className="page-dot" style={{background: '#a855f7'}} />
              <span style={{flex:1}}>{f.title}</span>
              <button className="page-del-btn" onClick={e => { e.stopPropagation(); setDuplicatingForm(f); }} title="Duplicate form" style={{fontSize:14,width:28,height:28,padding:0}}>⎘</button>
              <button className="page-del-btn" onClick={e => { e.stopPropagation(); handleDeleteForm(f._id); }} title="Delete form">×</button>
            </div>
          ))}
          {(!Array.isArray(forms) || forms.filter(f => !isFormsReferralsForm(f)).length === 0) && <div className="nav-empty">Legacy forms</div>}
        </nav>

        </div>

        <div className="sidebar-section">
        <div className="sidebar-label">Forms + Referrals</div>
        <nav className="page-nav">
          {Array.isArray(forms) && forms.filter(isFormsReferralsForm).map(f => (
            <div key={f._id} className={`page-item ${activeForm?._id === f._id && activeView === 'forms' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('forms');
                setActiveForm(f);
                setActivePage(null);
                setActiveTable(null);
              }}>
              <div className="page-dot" style={{background: '#a855f7'}} />
              <span style={{flex:1}}>{f.title}</span>
              <button className="page-del-btn" onClick={e => { e.stopPropagation(); setDuplicatingForm(f); }} title="Duplicate form" style={{fontSize:14,width:28,height:28,padding:0}}>⎘</button>
              <button className="page-del-btn" onClick={e => { e.stopPropagation(); handleDeleteForm(f._id); }} title="Delete form">×</button>
            </div>
          ))}
          <div
            className={`page-item ${activeView === 'referrals' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('referrals');
              setActiveForm(null);
              setActivePage(null);
              setActiveTable(null);
              setTab('editor');
            }}
          >
            <div className="page-dot" style={{background: '#f59e0b'}} />
            <span style={{flex:1}}>Referral Codes</span>
          </div>
        </nav>
        <button className="sidebar-btn" onClick={() => { setNewFormSection('forms_referrals'); setModal('addForm'); }}>+ New Form</button>
        <button className="sidebar-btn" onClick={() => setModal('changePassword')}>Change Password</button>
        </div>

        <div className="sidebar-section">
        <div className="sidebar-label">Form Submissions</div>
        <nav className="page-nav">
          {dataTables.map(t => (
            <div key={t._id} className={`page-item ${activeTable?._id === t._id && activeView === 'tables' ? 'active' : ''}`}
              onClick={() => { setActiveView('tables'); setActiveTable(t); setActivePage(null); setActiveForm(null); }}>
              <div className="page-dot" style={{background: '#06b6d4'}} />
              <span style={{flex:1}}>{t.name}</span>
              <button className="page-del-btn" onClick={e => { e.stopPropagation(); handleDeleteTable(t._id); }} title="Delete table">×</button>
            </div>
          ))}
          {dataTables.length === 0 && <div className="nav-empty">No tables yet</div>}
        </nav>
        <button className="sidebar-btn" onClick={() => setModal('addTable')}>+ New Table</button>
        </div>
      </aside>

      {activeView === 'pages' && (
      <section className="nav-panel">
        <div className="breadcrumb-bar">
          <div className="bc-left">
            {(docTree) && (
              <button className="back-btn" onClick={() => {
                if (navPath.length > 0) {
                  setNavPath(p => p.slice(0, -1));
                  setRightPane(null);
                } else {
                  setDocTree(null);
                  setNavPath([]);
                  setRightPane(null);
                }
              }}>‹</button>
            )}
            {docTree ? (
              <div className="breadcrumbs">
                {crumbs.map((c, i) => (
                  <span key={i} className="bc-item">
                    <button className={`bc-btn ${i === crumbs.length - 1 ? 'active' : ''}`}
                      onClick={() => { setNavPath(c.path); setRightPane(null); }}>
                      {c.label}
                    </button>
                    {i < crumbs.length - 1 && <span className="bc-sep">›</span>}
                  </span>
                ))}
              </div>
            ) : (
              <div className="bc-placeholder">{activePage ? activePage.name : 'Select a page'}</div>
            )}
          </div>
          {docTree && (
            <button className="icon-btn" onClick={() => setModal('addSection')} title="Add section">+</button>
          )}
          {!docTree && activePage && (
            <button className="icon-btn" onClick={() => setModal('addTopSection')} title="Add section">+</button>
          )}
        </div>

        <div className="nav-list">
          {!activePage && <div className="nav-empty-msg">← Select a page</div>}
          {activePage && !docTree && (
            <>
              {topSections.length === 0 && <div className="nav-empty-msg">No sections yet.<br/>Click + to add one.</div>}
              {topSections.map((s, i) => (
                <div key={s._id}
                  className={`nav-item ${dragOverId === s._id ? 'drag-over' : ''}`}
                  draggable
                  onDragStart={e => e.dataTransfer.setData('id', s._id)}
                  onDragOver={e => { e.preventDefault(); setDragOverId(s._id); }}
                  onDragLeave={() => setDragOverId(null)}
                  onDrop={e => {
                    e.preventDefault();
                    setDragOverId(null);
                    const draggedId = e.dataTransfer.getData('id');
                    if (draggedId === s._id) return;
                    const from = topSections.findIndex(x => x._id === draggedId);
                    const to = topSections.findIndex(x => x._id === s._id);
                    const newOrder = [...topSections];
                    const [moved] = newOrder.splice(from, 1);
                    newOrder.splice(to, 0, moved);
                    handleReorder(newOrder);
                  }}
                  onClick={() => handleTopSectionClick(s)}>
                  <span className="drag-handle" title="Drag to reorder">⠿</span>
                  <span className="nav-item-name">{s.label || s.title}</span>
                  <span className={`pill ${s.published ? 'pub' : 'draft'}`}>{s.published ? 'pub' : 'draft'}</span>
                  {userRole !== 'viewer' && <button className="edit-label-btn" onClick={e => { e.stopPropagation(); setEditingSectionLabel({...s, isTopSection: true}); }} title="Edit label">✎</button>}
                  <button className="rm-btn" onClick={e => { e.stopPropagation(); handleDeleteTopSection(s._id); }}>×</button>
                </div>
              ))}
            </>
          )}
          {docTree && currentNode && (
            <>
              {(currentNode.children || []).length === 0 && (
                <div className="nav-empty-msg">
                  No sub-sections here.<br/>
                  Click + to add one,<br/>
                  or add data fields on the right.
                </div>
              )}
              {(currentNode.children || []).map(child => {
                const freshChild = docTree ? getNodeByPath(docTree.tree, [...navPath, child.id]) || child : child;
                return (
                  <div key={child.id}
                    className={`nav-item ${rightPane?.id === child.id ? 'selected' : ''}`}
                    onClick={() => handleSectionClick(child)}>
                    <span className="nav-item-icon">
                      {(freshChild.children || []).length > 0 ? '⊟' : (freshChild.fields || []).length > 0 ? '◈' : '⊟'}
                    </span>
                    <span className="nav-item-name">{freshChild.label || freshChild.name}</span>
                    {(freshChild.children || []).length > 0 && <span className="nav-item-arrow">›</span>}
                    {(freshChild.fields || []).length > 0 && <span className="nav-item-count">{freshChild.fields.length}</span>}
                    {userRole !== 'viewer' && <button className="edit-label-btn" onClick={e => { e.stopPropagation(); setEditingSectionLabel({...freshChild, path: [...navPath, child.id]}); }} title="Edit label">✎</button>}
                    <button className="rm-btn" onClick={e => { e.stopPropagation(); handleDeleteChild(child.id); }}>×</button>
                  </div>
                );
              })}
              {(currentNode.children || []).length === 0 && (
                <button className="add-data-btn" onClick={() => {
                  setRightPane({ ...currentNode, path: navPath });
                  setModal('addField');
                }}>+ Add Data Field</button>
              )}
            </>
          )}
        </div>
      </section>
      )}

      <main className="editor">
        <div className="editor-bar">
          <div className="tabs">
            {onBack && <button className="back-to-dash" onClick={onBack}>← Projects</button>}
            {activeView === 'pages' && (
              <>
                <button className={`tab ${tab === 'editor' ? 'act' : ''}`} onClick={() => setTab('editor')}>Editor</button>
                <button className={`tab ${tab === 'api' ? 'act' : ''}`} onClick={() => setTab('api')}>API</button>
              </>
            )}
            {activeView === 'tables' && (
              <button className={`tab ${tab === 'editor' ? 'act' : ''}`} onClick={() => setTab('editor')}>Data</button>
            )}
            {activeView === 'crm' && (
              <button className={`tab ${tab === 'editor' ? 'act' : ''}`} onClick={() => setTab('editor')}>
                {crmSelectedLists.length === 1 && isCirclesList(crmSelectedLists[0]) ? 'Communities' : 'Members'}
              </button>
            )}
            {(activeView === 'forms' || activeView === 'referrals') && (
              <button className={`tab ${tab === 'editor' ? 'act' : ''}`} onClick={() => setTab('editor')}>
                {activeView === 'referrals' ? 'Forms + Referrals' : 'Form'}
              </button>
            )}
          </div>
          {docTree && activeView === 'pages' && tab === 'editor' && (
            <div className="editor-actions">
              {docTree.published && (
                <button className="btn-unpub" onClick={() => handleUnpublish()} disabled={saving}>
                  {saving ? 'Saving…' : 'Unpublish'}
                </button>
              )}
              <button className={`btn-pub ${docTree.published ? 'done' : ''}`}
                onClick={() => handleSave(true)} disabled={saving || docTree.published}>
                {docTree.published ? '✓ Published' : 'Publish'}
              </button>
            </div>
          )}
        </div>

        {activeView === 'tables' && tab === 'editor' && (
          <div className="editor-body">
            {activeTable && !isCrmTable(activeTable) ? (
              <SI3TableEditor
                table={activeTable}
                onAddRow={handleAddTableRow}
                onUpdateRow={handleUpdateTableRow}
                onDeleteRow={handleDeleteTableRow}
                onAddColumn={handleAddTableColumn}
                onUpdateColumn={handleUpdateTableColumn}
                onDeleteColumn={handleDeleteTableColumn}
                userRole={userRole}
              />
            ) : (
              <div className="editor-empty">
                <div className="empty-icon">⊡</div>
                <div>Select a table to view and manage data</div>
              </div>
            )}
          </div>
        )}

        {activeView === 'crm' && tab === 'editor' && (
          <div className="editor-body">
            {crmTable && (
              <div className="crm-group-tabs">
                <button
                  className={`crm-group-tab ${crmSelectedLists.length === 0 ? 'on' : ''}`}
                  onClick={() => openCrm([])}
                >
                  All Members
                </button>
                {crmLists.map((name) => (
                  <button
                    key={name}
                    className={`crm-group-tab ${crmSelectedLists.some((item) => crmNameKey(item) === crmNameKey(name)) ? 'on' : ''}`}
                    onClick={() => toggleCrmList(name)}
                  >
                    {name}
                  </button>
                ))}
                <div style={{width:'100%',fontSize:11,color:'var(--muted)',padding:'4px 0 8px'}}>
                  Select more than one list to see them together. Drag lists on the left to reorder. All Members shows everyone.
                </div>
              </div>
            )}
            {(activeTable && isCrmTable(activeTable) ? activeTable : crmTable) ? (
              <SI3TableEditor
                table={{
                  ...(activeTable && isCrmTable(activeTable) ? activeTable : crmTable),
                  columns: columnsForCrmView((activeTable && isCrmTable(activeTable) ? activeTable : crmTable).columns, crmSelectedLists, forms),
                  rows: ((activeTable && isCrmTable(activeTable) ? activeTable : crmTable).rows || []).filter((row) => rowInCrmLists(activeTable && isCrmTable(activeTable) ? activeTable : crmTable, row, crmSelectedLists))
                }}
                heading={crmSelectedLists.length ? crmSelectedLists.join(', ') : 'All Members'}
                onAddRow={handleAddTableRow}
                onUpdateRow={handleUpdateTableRow}
                onDeleteRow={handleDeleteTableRow}
                onAddColumn={handleAddTableColumn}
                onUpdateColumn={handleUpdateTableColumn}
                onDeleteColumn={handleDeleteTableColumn}
                userRole={userRole}
                listNames={crmLists}
                selectedLists={crmSelectedLists}
                onReviewRow={userRole !== 'viewer' ? handleReviewApplicant : null}
                onMarkPaid={userRole !== 'viewer' ? handleMarkPaid : null}
                rowLabel={crmSelectedLists.length === 1 && isCirclesList(crmSelectedLists[0]) ? 'Community' : 'Member'}
                extraActions={userRole !== 'viewer' ? (
                  <><button className="btn-add-col" style={{background:'transparent',color:'var(--text)',border:'1px solid var(--border2)'}} onClick={() => setShowCrmImport(true)}>Import CSV</button><button className="btn-add-col" style={{background:'transparent',color:'var(--text)',border:'1px solid var(--border2)'}} onClick={() => setShowCrmExport(true)}>Export CSV</button></>
                ) : null}
              />
            ) : (
              <div className="editor-empty">
                <div className="empty-icon">◈</div>
                <div>Create a list to add members, or import a CSV</div>
              </div>
            )}
          </div>
        )}

        {activeView === 'forms' && tab === 'editor' && (
          <div className="editor-body">
            {activeForm ? (
              <FormBuilder
                form={activeForm}
                onUpdate={handleUpdateForm}
                userRole={userRole}
                project={project}
                forms={forms}
              />
            ) : (
              <div className="editor-empty">
                <div className="empty-icon">📋</div>
                <div>Select a form to edit</div>
              </div>
            )}
          </div>
        )}

        {activeView === 'referrals' && tab === 'editor' && (
          <div className="editor-body">
            <ReferralsManager project={project} forms={forms} userRole={userRole} />
          </div>
        )}

        {tab === 'editor' && activeView === 'pages' && (
          <div className="editor-body">
            {!rightPane && (
              <div className="editor-empty">
                <div className="empty-icon">◈</div>
                <div>
                  {!activePage && 'Select a page to get started'}
                  {activePage && !docTree && 'Select a section from the panel'}
                  {docTree && !rightPane && 'Select a section that contains data fields'}
                </div>
              </div>
            )}

            {rightPane && (
              <>
                <div className="data-editor-header">
                  <div className="data-editor-title">
                    <span className="data-editor-icon">◈</span>
                    {rightPane.label || rightPane.name}
                  </div>
                  {userRole !== "viewer" && <button className="btn-add-field" onClick={() => setModal('addField')}>+ Add Field</button>}
                </div>

                {(rightPane.fields || []).length === 0 && (
                  <div className="no-fields">No fields yet. Click "+ Add Field" above.</div>
                )}

                {(hasChanges || autoSaveStatus) && (
                  <div className="inline-save-bar">
                    {autoSaveStatus === 'saving' && <span className="inline-save-msg">Auto-saving…</span>}
                    {autoSaveStatus === 'saved' && <span className="inline-save-msg" style={{color:'var(--accent)'}}>✓ Saved</span>}
                    {!autoSaveStatus && hasChanges && (
                      <>
                        <span className="inline-save-msg">Unsaved changes</span>
                        <button className="btn-ghost" onClick={handleDiscardInline}>Discard</button>
                        <button className="btn-solid" onClick={handleSaveInline}>Save</button>
                      </>
                    )}
                  </div>
                )}

                <div className="fields-list">
                  {(rightPane.fields || []).map(f => {
                    const val = localData[f.name];
                    const isArray = f.type === 'array';
                    const isImage = f.type === 'image';
                    const isText = f.type === 'text';
                    const isHtml = f.type === 'html';
                    const isBool = f.type === 'boolean';
                    const arrVal = localData[f.name] ?? (rightPane.data || {})[f.name];
                    return (
                      <div key={f.id || f.name} className="inline-field-row"
                        style={fieldDropTarget === f.id ? {boxShadow:'0 -2px 0 0 var(--accent)'} : undefined}
                        onDragOver={e => { if (draggedFieldId.current) { e.preventDefault(); if (fieldDropTarget !== f.id) setFieldDropTarget(f.id); } }}
                        onDrop={e => {
                          if (!draggedFieldId.current) return;
                          e.preventDefault();
                          e.stopPropagation();
                          const from = draggedFieldId.current;
                          draggedFieldId.current = null;
                          setFieldDropTarget(null);
                          handleReorderFields(from, f.id);
                        }}>
                        <div className="inline-field-header">
                          <div className="inline-field-label-row">
                            {userRole !== 'viewer' && (
                              <span className="drag-handle" draggable title="Drag to reorder field"
                                onDragStart={e => { draggedFieldId.current = f.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', f.id); }}
                                onDragEnd={() => { draggedFieldId.current = null; setFieldDropTarget(null); }}>⠿</span>
                            )}
                            <span className="field-row-icon">{typeIcon(f.type)}</span>
                            <span className="field-row-name">{f.label}</span>
                            <span className="badge">{f.type === 'array' ? `array · ${(f.subFields||[]).length} sub-fields` : f.type}</span>
                            {userRole !== 'viewer' && <button className="edit-label-btn" onClick={e => { e.stopPropagation(); setEditingFieldLabel(f); }} title="Edit label">✎</button>}
                          </div>
                          <div style={{display:'flex',gap:6}}>
                            {isArray && <button className="edit-schema-btn" onClick={e => { e.stopPropagation(); setEditingSchema(f); }}>Edit Fields</button>}
                            {isArray && userRole !== 'viewer' && <button className="edit-schema-btn" onClick={e => { e.stopPropagation(); setEditingArrayDisplayField(f); }} title="Set display field">Display Field</button>}
                            {userRole !== 'viewer' && <button className="rm-btn field-rm" onClick={e => { e.stopPropagation(); handleDeleteField(f.id); }}>×</button>}
                          </div>
                        </div>

                        {isArray && (
                          <div className="inline-array-area">
                            {Array.isArray(arrVal) && arrVal.length > 0 ? (
                              <div className="inline-cards-list">
                                {arrVal.map((item, idx) => (
                                  <div key={item._key || JSON.stringify(item)}
                                    className="inline-card-preview"
                                    draggable
                                    onDragStart={e => e.dataTransfer.setData('cardIdx', String(idx))}
                                    onDragOver={e => e.preventDefault()}
                                    onDrop={e => {
                                      e.preventDefault();
                                      const from = parseInt(e.dataTransfer.getData('cardIdx'), 10);
                                      const to = idx;
                                      if (from === to) return;
                                      const newArr = [...arrVal];
                                      const [moved] = newArr.splice(from, 1);
                                      newArr.splice(to, 0, moved);
                                      handleLocalChange(f.name, newArr);
                                    }}
                                    onClick={() => {
                                      console.log('Edit button clicked for array item');
                                      console.log('Field being passed:', f);
                                      console.log('Field subFields:', f.subFields);
                                      f.subFields?.forEach(sf => console.log(`  - ${sf.name}: previewTemplate="${sf.previewTemplate}"`));
                                      setEditingSingleItem({field: f, index: idx, item});
                                    }}>
                                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                                      <span className="drag-handle" title="Drag to reorder" onClick={e => e.stopPropagation()}>⠿</span>
                                      <div style={{flex:1}}>
                                        {(() => {
                                          const displayFieldName = f.displayField;
                                          const displaySubField = (f.subFields || []).find(sf => sf.name === displayFieldName);
                                          const fallbackSubField = (f.subFields || [])[0];
                                          const targetField = displaySubField || fallbackSubField;

                                          if (!targetField) return null;
                                          const v = item[targetField.name];
                                          return (
                                            <div className="inline-card-row">
                                              <span className="inline-card-key">{targetField.label || targetField.name}</span>
                                              <span className="inline-card-val">
                                                {typeof v === 'string' && (v.startsWith('http')||v.includes('/ipfs/'))
                                                  ? <img src={v} alt="" style={{width:40,height:40,objectFit:'cover',borderRadius:4}} onError={e=>e.target.style.display='none'} />
                                                  : String(v || '').slice(0,60)}{String(v || '').length>60?'...':''}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                        <div className="inline-card-edit-hint">click to edit · drag to reorder</div>
                                      </div>
                                      <button className="rm-btn" style={{flexShrink:0}} onClick={e => {
                                        e.stopPropagation();
                                        const newArr = arrVal.filter((_, j) => j !== idx);
                                        handleLocalChange(f.name, newArr);
                                      }}>×</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="inline-array-empty">No items yet</div>
                            )}
                            {userRole !== 'viewer' && (
                              <button className="inline-array-add-btn" onClick={() => setEditingField(f)}>+ Add Item</button>
                            )}
                          </div>
                        )}

                        {!isArray && userRole !== 'viewer' && (
                          <div className="inline-field-input">
                            {f.type === 'video' ? (
                              <div>
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                                  onClick={() => document.getElementById('inline-vid-'+f.id)?.click()}>
                                  <span style={{fontSize:12,color:'var(--muted2)'}}>▶ {val ? 'Replace video' : 'Upload video'}</span>
                                  <input id={'inline-vid-'+f.id} type="file" accept="video/*" style={{display:'none'}}
                                    onChange={async e => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const fd = new FormData(); fd.append('image', file);
                                      try {
                                        const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST',body:fd});
                                        const data = await res.json();
                                        if (data.url) handleLocalChange(f.name, data.url);
                                      } catch(err) { alert('Upload failed'); }
                                    }} />
                                </div>
                                {val && <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
                                  <video src={val} controls style={{width:200,height:120,objectFit:'cover',borderRadius:6,display:'block'}} />
                                  <button className="btn-ghost" style={{fontSize:11,padding:'5px 10px',width:'fit-content'}} onClick={() => handleLocalChange(f.name, '')}>Remove video</button>
                                </div>}
                              </div>
                            ) : f.type === 'file' ? (
                              <div>
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                                  onClick={() => document.getElementById('inline-file-'+f.id)?.click()}>
                                  <span style={{fontSize:12,color:'var(--muted2)'}}>📎 {val ? 'Replace file' : 'Upload file'}</span>
                                  <input id={'inline-file-'+f.id} type="file" style={{display:'none'}}
                                    onChange={async e => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const fd = new FormData(); fd.append('image', file);
                                      try {
                                        const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST',body:fd});
                                        const data = await res.json();
                                        if (data.url) handleLocalChange(f.name, data.url);
                                      } catch(err) { alert('Upload failed'); }
                                    }} />
                                </div>
                                {val && <div style={{marginTop:8,display:'flex',gap:8,alignItems:'center'}}>
                                  <div style={{fontSize:11,color:'var(--accent)'}}>✓ {val.split('/').pop()}</div>
                                  <button className="btn-ghost" style={{fontSize:11,padding:'5px 10px'}} onClick={() => handleLocalChange(f.name, '')}>Remove</button>
                                </div>}
                              </div>
                            ) : isImage ? (
                              <div>
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'7px 10px',border:'1px dashed var(--border2)',borderRadius:'var(--r-sm)',background:'var(--bg)',cursor:'pointer'}}
                                  onClick={() => document.getElementById('inline-img-'+f.id)?.click()}>
                                  <span style={{fontSize:12,color:'var(--muted2)'}}>⬆ {val ? 'Replace image' : 'Upload image'}</span>
                                  <input id={'inline-img-'+f.id} type="file" accept="image/*" style={{display:'none'}}
                                    onChange={async e => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      const fd = new FormData(); fd.append('image', file);
                                      try {
                                        const res = await fetch(process.env.REACT_APP_API_URL+'/api/upload', {method:'POST',body:fd});
                                        const data = await res.json();
                                        if (data.url) { handleLocalChange(f.name, data.url); }
                                      } catch(err) { alert('Upload failed'); }
                                    }} />
                                </div>
                                {val && <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:8}}>
                                  <img src={val} alt="" style={{width:200,height:130,objectFit:'cover',borderRadius:6,display:'block'}} onError={e=>e.target.style.display='none'} />
                                  <button className="btn-ghost" style={{fontSize:11,padding:'5px 10px',width:'fit-content'}} onClick={() => handleLocalChange(f.name, '')}>Remove image</button>
                                </div>}
                                {val && <div style={{fontSize:10,color:'var(--muted)',marginTop:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>✓ {val.includes('/ipfs/') ? 'Uploaded to Pinata' : 'Image set'}</div>}
                              </div>
                            ) : isBool ? (
                              <select className="minput inline-input" value={val === true || val === 'true' ? 'true' : 'false'} onChange={e => handleLocalChange(f.name, e.target.value === 'true')}>
                                <option value="true">True</option>
                                <option value="false">False</option>
                              </select>
                            ) : isHtml ? (
                                <div className="ql-wrapper">
                                  <ReactQuill
                                    theme="snow"
                                    value={val || ''}
                                    onChange={value => handleLocalChange(f.name, value)}
                                  />
                                </div>
                            ) : isText ? (
                              <textarea className="minput inline-input" rows={3} value={val || ''} onChange={e => handleLocalChange(f.name, e.target.value)} placeholder={`Enter ${f.label}...`} />
                            ) : f.type === 'string' ? (
                              <div>
                                <input className="minput inline-input" type="text"
                                  value={val || ''} onChange={e => handleLocalChange(f.name, e.target.value)}
                                  placeholder={`Enter ${f.label}...`} />
                                {f.previewTemplate && f.previewTemplate.trim() && val && (
                                  <div style={{marginTop:8,padding:8,background:'var(--bg)',borderRadius:4,fontSize:12,color:'var(--muted2)',border:'1px solid var(--border)'}}>
                                    <div style={{fontSize:11,color:'var(--muted)',marginBottom:4}}>Preview:</div>
                                    <div style={{fontSize:13,color:'var(--text)'}}>
                                      {f.previewTemplate}{val}
                                    </div>
                                  </div>
                                )}
                                {userRole !== 'viewer' && (
                                  <div style={{marginTop:6,display:'flex',gap:4,alignItems:'center',fontSize:11}}>
                                    <label style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer',color:'var(--accent)'}}>
                                      <input type="checkbox" checked={!!f.previewTemplate}
                                        onChange={e => {
                                          if (e.target.checked) {
                                            handleUpdateField(f.id, { previewTemplate: f.label + ': ' });
                                          } else {
                                            handleUpdateField(f.id, { previewTemplate: '' });
                                          }
                                        }} />
                                      Preview
                                    </label>
                                    {f.previewTemplate && (
                                      <input className="minput" style={{flex:1,fontSize:11,padding:'3px 6px'}} type="text"
                                        value={f.previewTemplate}
                                        onChange={e => handleUpdateField(f.id, { previewTemplate: e.target.value })}
                                        placeholder="e.g. Product: " />
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <input className="minput inline-input" type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                                value={val || ''} onChange={e => handleLocalChange(f.name, f.type === 'number' ? parseFloat(e.target.value)||0 : e.target.value)}
                                placeholder={`Enter ${f.label}...`} />
                            )}
                          </div>
                        )}

                        {!isArray && userRole === 'viewer' && (
                          <div className="inline-field-readonly">
                            {isHtml
                              ? <div dangerouslySetInnerHTML={{__html: val || ''}} style={{fontSize:13,color:'var(--muted2)'}} />
                              : val ? String(val) : <span style={{color:'var(--muted)'}}>No data</span>
                            }
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'api' && activeView === 'pages' && (
          <div className="editor-body">
            {!activePage ? <div className="editor-empty"><div>Select a page to see the API</div></div> : (
              <>
                <div className="api-block">
                  <div className="api-label">Endpoints</div>
                  <pre className="api-code">{`GET /api/query?q=*[_type == "${activePage.slug}"]
GET /api/query?q=*[_type == "${activePage.slug}"][0]
GET /api/query?q=*[_id == "DOCUMENT_ID"][0]`}</pre>
                </div>
                <div className="api-block">
                  <div className="api-label">Use in React / Next.js</div>
                  <pre className="api-code">{'// All sections for this page\n' +
`const res = await fetch('${process.env.REACT_APP_API_URL}/api/query?q=*[_type == "${activePage.slug}"]');\n` +
'const { result } = await res.json();\n\n' +
'// First section only\n' +
`const res2 = await fetch('${process.env.REACT_APP_API_URL}/api/query?q=*[_type == "${activePage.slug}"][0]');\n` +
'const { result: section } = await res2.json();'}</pre>
                </div>
                <div className="api-block">
                  <div className="api-label">Response</div>
                  <pre className="api-code">{JSON.stringify(topSections.filter(s => s.published).map(s => ({ _id: s._id, title: s.title, ...s.tree })), null, 2) || '// No published sections yet'}</pre>
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {reviewConfirm && (
        <ConfirmDialog
          title={reviewConfirm.title}
          message={reviewConfirm.message}
          confirmLabel={reviewConfirm.confirmLabel}
          busyLabel={reviewConfirm.busyLabel}
          busy={reviewConfirmBusy}
          onConfirm={confirmReviewAction}
          onCancel={cancelReviewConfirm}
        />
      )}

      {modal === 'addPage' && <NameModal title="New Page" placeholder="e.g. Home, About, Blog..." onConfirm={handleAddPage} onClose={() => setModal(null)} />}
      {modal === 'addCategory' && <NameModal title="New Category" placeholder="e.g. Marketing, Legal, Product..." onConfirm={handleAddCategory} onClose={() => setModal(null)} />}
      {modal === 'addTopSection' && <NameModal title="New Section" placeholder="e.g. Hero, Footer, Features..." onConfirm={handleAddTopSection} onClose={() => setModal(null)} showKey={true} />}
      {modal === 'addSection' && <NameModal title="New Sub-section" placeholder="e.g. Card 1, Link Group..." onConfirm={handleAddSubSection} onClose={() => setModal(null)} showKey={true} />}
      {modal === 'addField' && <AddFieldModal onConfirm={handleAddField} onClose={() => setModal(null)} />}
      {modal === 'addTable' && <NameModal title="New Table" placeholder="e.g. Products, Users, Orders..." onConfirm={handleAddTable} onClose={() => setModal(null)} />}
      {modal === 'addCrmCategory' && <NameModal title="New CRM List" placeholder="List name" onConfirm={handleAddCrmCategory} onClose={() => setModal(null)} />}
      {modal === 'renameCrm' && renamingCrm && (
        <NameModal
          title="Rename List"
          placeholder="List name"
          initialValue={typeof renamingCrm === 'string' ? renamingCrm : renamingCrm.name}
          confirmLabel="Rename"
          onConfirm={handleRenameCrmCategory}
          onClose={() => { setRenamingCrm(null); setModal(null); }}
        />
      )}
      {showCrmExport && (
        <CrmCsvExport
          lists={crmLists}
          selectedLists={crmSelectedLists}
          columns={crmTable?.columns || []}
          rows={crmTable?.rows || []}
          onClose={() => setShowCrmExport(false)}
        />
      )}
      {showCrmImport && (
        <CrmCsvImport
          lists={crmLists}
          selectedLists={crmSelectedLists}
          columns={crmTable?.columns || []}
          onImport={handleCrmImport}
          onClose={() => setShowCrmImport(false)}
        />
      )}
      {modal === 'addForm' && <AddFormModal onConfirm={handleAddForm} onClose={() => setModal(null)} />}
      {modal === 'changePassword' && <ChangePasswordModal onClose={() => setModal(null)} />}
      {duplicatingForm && <DuplicateFormModal form={duplicatingForm} onConfirm={(name, slug) => handleDuplicateForm(duplicatingForm._id, name, slug)} onClose={() => setDuplicatingForm(null)} />}
      {editingSchema && (
        <EditSubFieldsModal
          field={editingSchema}
          docTree={docTree}
          rightPane={rightPane}
          onConfirm={handleUpdateSubFields}
          onClose={() => setEditingSchema(null)}
        />
      )}
      {editingSingleItem && rightPane && (
        <EditSingleItemModal
          field={editingSingleItem.field}
          item={editingSingleItem.item}
          onSave={updatedItem => {
            const arrName = editingSingleItem.field.name;
            const arr = [...((rightPane.data || {})[arrName] || [])];
            arr[editingSingleItem.index] = { ...arr[editingSingleItem.index], ...updatedItem };
            handleLocalChange(arrName, arr);
            setEditingSingleItem(null);
          }}
          onClose={() => setEditingSingleItem(null)}
        />
      )}
      {showAI && activePage && (
        <AIAssist
          onApply={async ({ sectionName, fields }) => {
            if (!activePage) return;
            const builtFields = fields
              .filter(f => f.type !== 'array')
              .map(f => ({ id: uid(), name: f.name, label: f.name, type: f.type }));
            const builtArrayFields = fields
              .filter(f => f.type === 'array')
              .map(f => ({
                id: uid(), name: f.name, label: f.name, type: 'array',
                subFields: (f.subFields || []).map(sf => ({ id: uid(), name: sf, label: sf, type: 'string' }))
              }));
            const allFields = [...builtFields, ...builtArrayFields];
            const data = {};
            fields.forEach(f => {
              if (f.type === 'array') data[f.name] = [];
              else if (f.type === 'boolean') data[f.name] = false;
              else if (f.type === 'number') data[f.name] = 0;
              else data[f.name] = f.value || '';
            });
            const existingKeys = topSections.map(s => s.tree?._key || '');
            if (existingKeys.includes(sectionName)) {
              alert('Section key already exists. Please rename it in the review step.');
              return;
            }
            const newTree = { id: uid(), _key: sectionName, name: sectionName, label: sectionName, children: [], fields: allFields, data };
            try {
              const res = await API.post('/api/documents', {
                schemaId: activePage._id, schemaSlug: activePage.slug,
                title: sectionName, fields: allFields, data: { tree: newTree }, published: false
              });
              const doc = { _id: res.data._id, title: sectionName, published: false, tree: newTree };
              setTopSections(p => [...p, doc]);
            } catch(e) { alert('Error creating section'); }
            setShowAI(false);
          }}
          onClose={() => setShowAI(false)}
        />
      )}
      {editingField && rightPane && (
        <EditFieldModal
          field={editingField}
          value={(rightPane.data || {})[editingField.name]}
          onSave={val => { handleLocalChange(editingField.name, val); setEditingField(null); }}
          onAutoSave={val => handleFieldSave(editingField.name, val)}
          onEditSubFields={f => { setEditingField(null); setEditingSchema(f); }}
          onClose={() => setEditingField(null)}
        />
      )}
      {editingFieldLabel && (
        <EditFieldLabelModal
          field={editingFieldLabel}
          onConfirm={handleUpdateFieldLabel}
          onClose={() => setEditingFieldLabel(null)}
        />
      )}
      {editingArrayDisplayField && (
        <EditArrayDisplayFieldModal
          field={editingArrayDisplayField}
          onConfirm={handleUpdateArrayDisplayField}
          onClose={() => setEditingArrayDisplayField(null)}
        />
      )}
      {editingSectionLabel && (
        <Modal onClose={() => setEditingSectionLabel(null)}>
          <div className="modal-title">Edit Section Label</div>
          <label className="mlabel">Display Name</label>
          <input className="minput"
            defaultValue={editingSectionLabel.label || ''}
            placeholder={editingSectionLabel.isTopSection ? editingSectionLabel.title : editingSectionLabel.name}
            onKeyDown={e => e.key === 'Enter' && handleUpdateSectionLabel(e.target.value)}
            autoFocus />
          <div style={{fontSize:11,color:'var(--muted)',marginTop:8}}>
            Section "{editingSectionLabel.isTopSection ? editingSectionLabel.title : editingSectionLabel.name}" will remain unchanged
          </div>
          <div className="mactions">
            <button className="btn-ghost" onClick={() => setEditingSectionLabel(null)}>Cancel</button>
            <button className="btn-solid" onClick={(e) => {
              const input = e.target.closest('.modal').querySelector('input');
              handleUpdateSectionLabel(input.value);
            }}>Update</button>
          </div>
        </Modal>
      )}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`} role={toast.type === 'error' ? 'alert' : 'status'}>
            <span className="toast-message">{toast.message}</span>
            <button type="button" className="toast-close" aria-label="Dismiss" onClick={() => setToasts(current => current.filter(item => item.id !== toast.id))}>×</button>
          </div>
        ))}
      </div>
    </div>
    </SpeakerNamesContext.Provider>
  );
}